import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Send, CheckCircle, XCircle, MessageSquare, AlertCircle, Shield, Lock,
  ChevronDown, ChevronUp, Edit3, FileText, DollarSign, Package, Star,
  Calendar, Bookmark, Zap, Eye, ChevronRight, AlertTriangle, Layers, Clock,
  Sparkles, Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import API from '@/lib/api';

// ═══════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════

const PLATS = ['Instagram', 'TikTok', 'YouTube', 'Twitter/X', 'Facebook', 'LinkedIn', 'Podcast', 'Website'];
const TERRS = ['US', 'UK', 'EU', 'Canada', 'APAC', 'LATAM', 'Middle East', 'Global'];
const DELIVERABLE_TYPES = ['image_ads', 'short_videos', 'long_form_video', 'stories', 'reels', 'voiceovers', 'promo_stills', 'podcast_spots', 'blog_content'];
const EXTRA_LIKENESS_OPTIONS = [
  { value: 'pronunciation_script', label: 'Pronunciation Script' },
  { value: 'movement_reference', label: 'Movement Reference' },
  { value: 'wardrobe_look_reference', label: 'Wardrobe / Look Reference' },
  { value: 'specific_styling_guidance', label: 'Specific Styling Guidance' },
  { value: 'additional_video_references', label: 'Additional Video References' },
  { value: 'additional_audio_references', label: 'Additional Audio References' },
  { value: 'campaign_specific_notes', label: 'Campaign-Specific Notes' },
];

const DEAL_STRUCTURES = [
  { value: 'one_time', label: 'One-Time Deal', desc: 'Single payment for a fixed campaign license.' },
  { value: 'subscription', label: 'Subscription License', desc: 'Ongoing recurring payment for continuous generative rights.' },
  { value: 'hybrid', label: 'Hybrid', desc: 'One-time base payment plus an ongoing subscription layer.' },
];

const SIGNON_TIMINGS = [
  { value: 'on_signing', label: 'On signing' },
  { value: 'on_first_asset', label: 'On first asset delivery' },
  { value: 'on_campaign_launch', label: 'On campaign launch' },
];

const EXCLUSIVITY_OPTIONS = [
  { value: 'non_exclusive', label: 'Non-Exclusive', desc: 'Creator may license to others simultaneously.' },
  { value: 'category_exclusive', label: 'Category Exclusive', desc: 'Exclusive within a defined product or industry category only.' },
  { value: 'full_exclusive', label: 'Full Exclusive', desc: 'No other brand may license this creator during the term.' },
];

const EXCLUSIVITY_CATEGORIES = [
  'Automotive', 'Alcohol & Spirits', 'Beauty & Cosmetics', 'Entertainment',
  'Fashion & Apparel', 'Financial Services', 'Food & Beverage', 'Gaming',
  'Health & Wellness', 'Luxury Goods', 'Pharmaceuticals', 'Retail',
  'Sportswear', 'Technology', 'Telecommunications', 'Travel & Hospitality',
];

const DURATION_TYPES = [
  { value: 'campaign_locked', label: 'Campaign-Locked', desc: 'Valid for proposal term dates only.' },
  { value: 'fixed', label: 'Fixed Term', desc: 'Custom start and end date.' },
  { value: 'rolling', label: 'Rolling', desc: 'Auto-renews on set interval until cancelled.' },
  { value: 'perpetual', label: 'Perpetual', desc: 'No expiration. Uncommon — may affect creator willingness.' },
];

const EXPIRY_BEHAVIORS = [
  { value: 'revert', label: 'Revert to Creator' },
  { value: 'negotiate', label: 'Negotiate Before Expiry' },
  { value: 'auto_renew', label: 'Auto-Renew (Rolling only)' },
];

const SUBLICENSING_OPTIONS = [
  { value: 'not_permitted', label: 'Not Permitted' },
  { value: 'subsidiaries_only', label: 'Subsidiaries Only' },
  { value: 'with_consent', label: 'With Written Consent' },
];

const PURPOSE_OF_USE = [
  { value: 'product_advertising', label: 'Product / Service Advertising' },
  { value: 'brand_awareness', label: 'Brand Awareness' },
  { value: 'organic_social', label: 'Organic Social Content' },
  { value: 'promotional_sales', label: 'Promotional / Sales-Driven' },
  { value: 'event_promotion', label: 'Event Promotion' },
  { value: 'retail_pos', label: 'Retail / Point-of-Sale' },
  { value: 'internal_corporate', label: 'Internal Corporate (non-public)' },
  { value: 'pr_press', label: 'PR / Press Materials' },
  { value: 'sponsorship_announcement', label: 'Sponsorship Announcement' },
  { value: 'charity_cause', label: 'Charity / Cause' },
  { value: 'recruitment_employer', label: 'Recruitment / Employer Branding' },
];

const CONTENT_RESTRICTIONS_LOCKED = [
  { value: 'sexual_content', label: 'Sexual or adult content' },
  { value: 'minors_sensitive', label: 'Minors in sensitive contexts' },
  { value: 'illegal_activity', label: 'Depiction of illegal activity' },
];

const CONTENT_RESTRICTIONS_OPTIONAL = [
  { value: 'political', label: 'Political content / candidate association' },
  { value: 'religious', label: 'Religious content' },
  { value: 'violence', label: 'Violence / conflict depictions' },
  { value: 'competitor_brands', label: 'Competitor brand association' },
  { value: 'alcohol_tobacco', label: 'Alcohol or tobacco' },
  { value: 'pharmaceutical', label: 'Pharmaceutical / medical claims' },
  { value: 'financial', label: 'Financial / investment promotion' },
  { value: 'gambling', label: 'Gambling / betting' },
  { value: 'personal_views', label: 'Implied personal views or beliefs' },
  { value: 'satire_parody', label: 'Satire or parody / comedic misrepresentation' },
  { value: 'news_editorial', label: 'News / editorial / documentary-style' },
];

const OUTPUT_REUSE_OPTIONS = [
  { value: 'not_permitted', label: 'Not Permitted', desc: 'All generated assets must be taken down on expiry.' },
  { value: 'existing_placements', label: 'Existing Placements Only', desc: 'Live assets may remain, no new placements.' },
  { value: 'new_agreement', label: 'With New Agreement', desc: 'Continued use requires a new license.' },
];

const SOURCE_REUSE_OPTIONS = [
  { value: 'not_permitted', label: 'Not Permitted', desc: 'Source materials may not be retained or used.' },
  { value: 'reference_only', label: 'Reference Only', desc: 'Retain internally; no new content generation.' },
  { value: 'new_agreement', label: 'With New Agreement', desc: 'Reusable only under a new license.' },
];

const ASSET_OWNERSHIP_OPTIONS = [
  { value: 'agency_owns_all', label: 'Agency Owns All', desc: 'Full ownership of output transfers to agency.' },
  { value: 'creator_retains_license', label: 'Creator Retains, Agency Licenses', desc: 'Creator owns the output; agency is granted a license to use.' },
  { value: 'joint', label: 'Joint Ownership', desc: 'Both parties hold equal rights.' },
  { value: 'agency_owns_creator_portfolio', label: 'Agency Owns + Creator Portfolio Rights', desc: 'Agency owns; creator may display in portfolio for self-promotion.' },
];

const MORAL_RIGHTS_OPTIONS = [
  { value: 'fully_reserved', label: 'Fully Reserved', desc: 'Creator retains the right to object to harmful uses.' },
  { value: 'partially_waived', label: 'Partially Waived', desc: 'Creator waives objection within agreed content scope.' },
  { value: 'waived_for_campaign', label: 'Waived for Campaign', desc: 'Creator agrees not to object within agreed parameters.' },
];

// Jurisdictions where moral-rights waiver is unenforceable
const MORAL_RIGHTS_STRONG = ['FR', 'DE', 'IT', 'ES', 'BE', 'NL', 'EU'];

// ── 4.2 AI Generation Permissions ──
const GENERATION_TYPES = [
  { value: 'static_image', label: 'Static Image' },
  { value: 'short_video', label: 'Short-Form Video (<60s)' },
  { value: 'long_video', label: 'Long-Form Video (≥60s)' },
  { value: 'voice_audio', label: 'Voice & Audio Only' },
  { value: 'audio_visual', label: 'Combined Audio-Visual' },
  { value: 'animated', label: 'Animated / Motion Graphics' },
  { value: 'avatar', label: 'Avatar / Virtual Representation' },
  { value: 'interactive_ai', label: 'Interactive / Real-Time AI' },
];

const VISUAL_REPRESENTATION_OPTIONS = [
  { value: 'realistic', label: 'Realistic Only', desc: 'Photorealistic and true to creator\'s actual appearance.' },
  { value: 'stylized', label: 'Stylized but Recognizable', desc: 'Creative interpretation while remaining identifiable.' },
  { value: 'fictional', label: 'Fictional / Fantasy', desc: 'May appear in non-realistic or fantastical settings.' },
];

const PERMITTED_ALTERATIONS = [
  { value: 'wardrobe_styling', label: 'Wardrobe / styling changes' },
  { value: 'background_setting', label: 'Setting / background changes' },
  { value: 'lighting_color', label: 'Lighting / color grading' },
  { value: 'hair', label: 'Hair color / style changes' },
  { value: 'skin_tone', label: 'Skin tone adjustments', sensitive: true, sensitivityNote: 'This is a sensitive alteration. Ensure creator is aware and consenting.' },
  { value: 'body_composition', label: 'Body composition changes', sensitive: true, sensitivityNote: 'High sensitivity alteration.' },
  { value: 'facial_features', label: 'Facial feature alterations', sensitive: true, sensitivityNote: 'High sensitivity alteration.' },
  { value: 'age_alteration', label: 'Age alteration', deliberate: true, sensitivityNote: 'Requires deliberate ENABLE confirmation.' },
  { value: 'gender_presentation', label: 'Gender presentation changes', deliberate: true, sensitivityNote: 'Requires deliberate ENABLE confirmation.' },
];

const STATEMENT_SCOPES = [
  { value: 'product_endorsement', label: 'Product endorsement only' },
  { value: 'agreed_script', label: 'Agreed script only' },
  { value: 'within_parameters', label: 'Within campaign parameters' },
  { value: 'other', label: 'Other (custom)' },
];

const POST_GEN_MODIFICATIONS = [
  { value: 'color_filters', label: 'Color grading & filters' },
  { value: 'overlays_logos', label: 'Text overlays, logos, graphic elements' },
  { value: 'timing_pacing', label: 'Video timing & pacing edits' },
  { value: 'compositing', label: 'Compositing into other footage / backgrounds' },
  { value: 'audio_dubbing', label: 'Audio dubbing / translation' },
  { value: 'cropping_reformatting', label: 'Cropping / reformatting for platforms' },
];

// ── 4.5 Subscription License Terms ──
const BILLING_FREQUENCIES = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annually', label: 'Annually' },
];

const UNUSED_ALLOWANCE = [
  { value: 'expires', label: 'Expires', desc: 'Unused allowance does not carry over.' },
  { value: 'rolls_over', label: 'Rolls Over', desc: 'Unused allowance carries to the next period.' },
];

const CONTENT_REUSE_SCOPE = [
  { value: 'new_generations_only', label: 'New generations only', desc: 'Republishing existing assets does not count toward the cap.' },
  { value: 'new_and_republishing', label: 'New generations + republishing', desc: 'Both count toward the monthly cap.' },
];

const SUBSCRIPTION_EXCLUSIVITY = [
  { value: 'non_exclusive', label: 'Non-Exclusive', desc: 'Creator may hold simultaneous subscriptions with other brands.' },
  { value: 'category_exclusive', label: 'Category Exclusive', desc: 'No competing brand subscription in the same category.' },
  { value: 'full_exclusive', label: 'Fully Exclusive', desc: 'Creator may not hold any other active subscription license during this term.' },
];

const PRICE_CHANGE_POLICY = [
  { value: 'fixed', label: 'Fixed Rate', desc: 'Subscription fee cannot change without a new agreement.' },
  { value: 'agency_with_notice', label: 'Agency May Adjust with Notice', desc: 'Agency may change rate with prior notice before next billing period.' },
  { value: 'mutually_agreed', label: 'Mutually Agreed', desc: 'Rate changes require both parties to agree in writing.' },
];

const CANCELLATION_FEE = [
  { value: 'full_period', label: 'Full Current Period' },
  { value: 'prorated', label: 'Prorated to Cancellation Date' },
  { value: 'none', label: 'No Further Fee' },
];

const ACCUMULATED_ASSETS_POLICY = [
  { value: 'must_takedown', label: 'Must Be Taken Down', desc: 'All assets removed within the takedown window.' },
  { value: 'remain_live', label: 'May Remain Live', desc: 'Already-published assets may stay; no new generation.' },
  { value: 'remain_live_period', label: 'Remain Live for a Defined Period', desc: 'Assets remain live for the period below, then must be removed.' },
];

// ── 4.6 Termination & Expiry ──
const TERMINATION_TRIGGERS = [
  { value: 'material_breach', label: 'Creator is in material breach of any representation or warranty' },
  { value: 'damaging_statements', label: 'Creator makes public statements materially damaging to the campaign or brand' },
  { value: 'exclusivity_violation', label: 'Creator violates agreed exclusivity terms' },
  { value: 'public_disavowal', label: 'Creator publicly disavows the campaign or brand during the term' },
];

const CURE_PERIODS = [
  { value: 'none', label: 'No cure period' },
  { value: '48h', label: '48 hours' },
  { value: '7d', label: '7 days' },
  { value: '14d', label: '14 days' },
  { value: '30d', label: '30 days' },
];

const MORALITY_CONDITIONS = [
  { value: 'criminal', label: 'Criminal charge or conviction' },
  { value: 'inconsistent_statements', label: 'Public statements inconsistent with brand values' },
  { value: 'discriminatory', label: 'Discriminatory, hateful, or harmful public behavior' },
  { value: 'negative_press', label: 'Conduct generating significant negative press coverage' },
];

const MORALITY_CONSEQUENCES = [
  { value: 'immediate', label: 'Immediate termination, no fee owed for remaining term' },
  { value: 'payment_for_work', label: 'Termination with payment for work completed to date' },
  { value: 'suspend_review', label: 'Agency right to suspend and review before terminating' },
];

const EARLY_TERMINATION_COMPENSATION = [
  { value: 'full', label: 'Full fee still owed' },
  { value: 'prorated', label: 'Fee prorated to termination date' },
  { value: 'kill_fee', label: 'Kill fee (% of total agreed fee)' },
  { value: 'none', label: 'No further compensation owed' },
];

const RESIDUAL_USE_CHANNELS = [
  'Print', 'Out-of-Home', 'Retail / POS', 'Trade Materials', 'Internal',
];

const DISPUTE_RESOLUTION = [
  { value: 'litigation', label: 'Litigation', desc: 'Through courts in governing jurisdiction.' },
  { value: 'arbitration', label: 'Binding Arbitration', desc: 'Private arbitration.' },
  { value: 'mediation_arbitration', label: 'Mediation, then Arbitration', desc: 'Mediate first; arbitrate if unresolved.' },
  { value: 'mediation_litigation', label: 'Mediation, then Litigation', desc: 'Mediate first; litigate if unresolved.' },
];

const GOVERNING_LAW_OPTIONS = [
  { value: 'US-CA', label: 'United States — California' },
  { value: 'US-NY', label: 'United States — New York' },
  { value: 'US-TX', label: 'United States — Texas' },
  { value: 'US', label: 'United States — Other' },
  { value: 'UK', label: 'United Kingdom' },
  { value: 'EU', label: 'European Union' },
  { value: 'FR', label: 'France' },
  { value: 'DE', label: 'Germany' },
  { value: 'IT', label: 'Italy' },
  { value: 'CA', label: 'Canada' },
  { value: 'AU', label: 'Australia' },
  { value: 'OTHER', label: 'Other' },
];

// ═══════════════════════════════════════════════════════════
// SHARED HELPERS
// ═══════════════════════════════════════════════════════════

function StatusBadge({ status }) {
  const map = {
    draft: 'bg-slate-50 text-slate-500 border-slate-200',
    none: 'bg-slate-50 text-slate-400 border-slate-200',
    sent: 'bg-blue-50 text-blue-600 border-blue-200',
    accepted: 'bg-emerald-50 text-emerald-600 border-emerald-200',
    rejected: 'bg-red-50 text-red-600 border-red-200',
    countered: 'bg-purple-50 text-purple-600 border-purple-200',
  };
  return (
    <Badge variant="outline" className={`text-[10px] font-semibold capitalize ${map[status] || 'bg-slate-50 text-slate-400'}`}>
      {(status || 'none').replace(/_/g, ' ')}
    </Badge>
  );
}

function ChipSelect({ options, selected, onChange, label, testid }) {
  return (
    <div data-testid={testid}>
      {label && <Label className="text-xs text-slate-600 mb-1.5 block">{label}</Label>}
      <div className="flex flex-wrap gap-1.5">
        {options.map(opt => {
          const val = typeof opt === 'string' ? opt : opt.value;
          const lbl = typeof opt === 'string' ? opt.replace(/_/g, ' ') : opt.label;
          const active = selected.includes(val);
          return (
            <button key={val} type="button"
              onClick={() => onChange(active ? selected.filter(x => x !== val) : [...selected, val])}
              className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-all capitalize
                ${active ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-500 border-slate-200 hover:border-blue-300'}`}>
              {lbl}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SectionToggle({ id, title, icon: Icon, open, onToggle, complete, required, children, badge }) {
  const dot = complete ? 'bg-emerald-500' : required ? 'bg-amber-400' : 'bg-slate-200';
  const dotLabel = complete ? 'Complete' : required ? 'Required' : 'Optional';
  return (
    <div id={`section-${id}`} className="border border-slate-100 rounded-xl bg-white overflow-hidden scroll-mt-24">
      <button onClick={onToggle} type="button" data-testid={`toggle-${id}`}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${dot}`} title={dotLabel} />
          <Icon className="w-4 h-4 text-slate-500" />
          <span className="text-sm font-semibold text-slate-800">{title}</span>
          {badge}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {open && <div className="px-4 pb-4 pt-1 border-t border-slate-50">{children}</div>}
    </div>
  );
}

function PreFilledBanner({ message }) {
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50/60 border border-blue-100 rounded-md mb-3">
      <Layers className="w-3 h-3 text-blue-500" />
      <p className="text-[11px] text-blue-700">{message}</p>
    </div>
  );
}

function InfoCell({ label, value }) {
  return (
    <div>
      <p className="text-[10px] text-slate-400 uppercase tracking-wider">{label}</p>
      <p className="text-xs font-medium text-slate-700 mt-0.5 capitalize">{value || '—'}</p>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// PLAIN-LANGUAGE SUMMARY GENERATOR
// ═══════════════════════════════════════════════════════════

function pretty(val) {
  if (!val) return '';
  return String(val).replace(/_/g, ' ');
}

function joinList(arr) {
  if (!arr || arr.length === 0) return '';
  if (arr.length === 1) return arr[0];
  if (arr.length === 2) return `${arr[0]} and ${arr[1]}`;
  return `${arr.slice(0, -1).join(', ')}, and ${arr[arr.length - 1]}`;
}

export function generatePlainSummary({ brief, offer, rights }) {
  const lines = [];
  if (brief?.objective) lines.push(`This proposal is for ${brief.objective}.`);

  // Commercial
  const ds = offer?.deal_structure || 'one_time';
  const cur = offer?.currency || 'USD';
  if (ds === 'one_time' && offer?.base_amount) {
    lines.push(`The agency is offering a one-time payment of ${cur} ${Number(offer.base_amount).toLocaleString()}.`);
  } else if (ds === 'subscription' && offer?.recurring_amount) {
    lines.push(`The agency is offering a subscription of ${cur} ${Number(offer.recurring_amount).toLocaleString()} per billing period.`);
  } else if (ds === 'hybrid') {
    const parts = [];
    if (offer.base_amount) parts.push(`a base payment of ${cur} ${Number(offer.base_amount).toLocaleString()}`);
    if (offer.recurring_amount) parts.push(`a recurring fee of ${cur} ${Number(offer.recurring_amount).toLocaleString()}`);
    if (parts.length) lines.push(`The agency is offering a hybrid deal with ${joinList(parts)}.`);
  }
  if (offer?.signon_bonus_amount) {
    const timing = SIGNON_TIMINGS.find(t => t.value === offer.signon_bonus_timing)?.label.toLowerCase() || 'on signing';
    lines.push(`A sign-on bonus of ${cur} ${Number(offer.signon_bonus_amount).toLocaleString()} will be paid ${timing}.`);
  }

  // 4.1 License Scope
  const excl = EXCLUSIVITY_OPTIONS.find(e => e.value === rights?.exclusivity);
  if (excl) {
    let line = `The license is ${excl.label.toLowerCase()}.`;
    if (rights.exclusivity === 'category_exclusive' && rights.exclusivity_categories?.length) {
      line += ` Exclusive within: ${joinList(rights.exclusivity_categories)}.`;
    }
    lines.push(line);
  }
  if (rights?.territory?.length) {
    lines.push(`Territory: ${joinList(rights.territory)}.`);
  }
  const dur = DURATION_TYPES.find(d => d.value === rights?.duration_type);
  if (dur) {
    let line = `Duration is ${dur.label.toLowerCase()}`;
    if (rights.term_start && rights.term_end) line += ` from ${rights.term_start} to ${rights.term_end}`;
    if (rights.duration_type === 'rolling' && rights.rolling_interval) line += `, renewing ${rights.rolling_interval}`;
    line += '.';
    lines.push(line);
  }
  if (rights?.expiry_behavior) {
    lines.push(`On expiry, rights ${rights.expiry_behavior === 'revert' ? 'revert to the creator' : rights.expiry_behavior === 'auto_renew' ? 'auto-renew' : 'will be renegotiated'}.`);
  }
  if (rights?.sublicensing) {
    const subMap = { not_permitted: 'Sublicensing is not permitted.', subsidiaries_only: 'Sublicensing is permitted to subsidiaries only.', with_consent: 'Sublicensing is permitted with creator written consent.' };
    lines.push(subMap[rights.sublicensing]);
  }

  // 4.3 Permitted Use
  if (rights?.purpose_of_use?.length) {
    const labels = rights.purpose_of_use.map(p => PURPOSE_OF_USE.find(o => o.value === p)?.label?.toLowerCase() || pretty(p));
    lines.push(`Permitted use: ${joinList(labels)}.`);
  }
  if (rights?.usage_scope) {
    lines.push(rights.usage_scope === 'reusable' ? 'Licensed assets may be reused across future campaigns under a new agreement.' : 'Licensed assets may only be used for this specific campaign.');
  }
  if (rights?.competitor_restriction && rights.competitor_names) {
    lines.push(`Will not reference or promote: ${rights.competitor_names}.`);
  }
  const restr = (rights?.content_restrictions || []).filter(r => !CONTENT_RESTRICTIONS_LOCKED.find(l => l.value === r));
  const lockedAlways = CONTENT_RESTRICTIONS_LOCKED.map(l => l.label.toLowerCase());
  if (restr.length) {
    const restrLabels = restr.map(r => CONTENT_RESTRICTIONS_OPTIONAL.find(o => o.value === r)?.label?.toLowerCase() || pretty(r));
    lines.push(`Content will not include: ${joinList([...lockedAlways, ...restrLabels])}.`);
  } else {
    lines.push(`Content will not include: ${joinList(lockedAlways)}.`);
  }
  if (rights?.output_reuse) {
    const map = { not_permitted: 'After the term, all generated assets must be taken down.', existing_placements: 'After the term, already-live assets may remain but no new placements are permitted.', new_agreement: 'After the term, generated assets may continue to be used only with a new agreement.' };
    lines.push(map[rights.output_reuse]);
  }
  if (rights?.source_material_reuse) {
    const map = { not_permitted: 'Source materials may not be retained after the term.', reference_only: 'Source materials may be retained internally for reference only.', new_agreement: 'Source materials may be reused only under a new license.' };
    lines.push(map[rights.source_material_reuse]);
  }

  // 4.4 Asset Ownership
  const owner = ASSET_OWNERSHIP_OPTIONS.find(o => o.value === rights?.asset_ownership);
  if (owner) lines.push(`${owner.desc}`);
  if (rights?.creator_portfolio_rights) lines.push(`Creator may display generated assets in their own portfolio.`);
  if (rights?.moral_rights) {
    const map = { fully_reserved: 'Creator retains full moral rights.', partially_waived: 'Creator partially waives moral rights within agreed scope.', waived_for_campaign: 'Creator waives moral rights for content produced within agreed parameters.' };
    lines.push(map[rights.moral_rights]);
  }
  if (rights?.endorsement_requested) {
    lines.push(`This proposal requests use in a context implying personal endorsement of the brand.`);
  }

  // 4.2 AI Generation Permissions
  if (rights?.generation_types?.length) {
    const labels = rights.generation_types.map(t => GENERATION_TYPES.find(g => g.value === t)?.label || pretty(t));
    lines.push(`Permitted generation types: ${joinList(labels)}.`);
  }
  if (rights?.visual_representation) {
    const vr = VISUAL_REPRESENTATION_OPTIONS.find(o => o.value === rights.visual_representation);
    if (vr) lines.push(`Visual representation: ${vr.label.toLowerCase()}.`);
  }
  if (rights?.permitted_alterations?.length) {
    const altLabels = rights.permitted_alterations.map(a => PERMITTED_ALTERATIONS.find(o => o.value === a)?.label?.toLowerCase() || pretty(a));
    lines.push(`Permitted alterations: ${joinList(altLabels)}.`);
  }
  if (rights?.dialogue_permitted) {
    let line = 'Generated content may include the creator\'s synthesized voice';
    if (rights.dialogue_scripted_only) line += ' (scripted lines only, scripts agreed in campaign brief)';
    line += '.';
    lines.push(line);
    if (rights.statements_permitted) {
      const scope = STATEMENT_SCOPES.find(s => s.value === rights.statement_scope)?.label || pretty(rights.statement_scope);
      const detail = rights.statement_scope === 'other' && rights.statement_scope_other ? ` (${rights.statement_scope_other})` : '';
      lines.push(`Creator may appear to make statements limited to: ${scope}${detail}.`);
    }
  }
  if (rights?.post_generation_modifications?.length) {
    if (rights.post_generation_modifications.includes('none')) {
      lines.push('No post-generation modifications permitted — assets used as-is.');
    } else {
      const modLabels = rights.post_generation_modifications.map(m => POST_GEN_MODIFICATIONS.find(o => o.value === m)?.label?.toLowerCase() || pretty(m));
      lines.push(`Post-generation modifications permitted: ${joinList(modLabels)}.`);
    }
  }

  // 4.5 Subscription License Terms (when applicable)
  const subActive = offer?.deal_structure === 'subscription' || offer?.deal_structure === 'hybrid';
  if (subActive) {
    lines.push('');
    lines.push('Subscription terms:');
    lines.push(`• Billed ${rights.subscription_billing_frequency || 'monthly'}.`);
    if (rights.generation_caps_enabled) {
      const caps = Object.entries(rights.generation_caps || {})
        .map(([t, v]) => {
          const label = GENERATION_TYPES.find(g => g.value === t)?.label || pretty(t);
          return v.unlimited ? `${label}: unlimited` : `${label}: ${v.cap || '0'} per period`;
        });
      if (caps.length) lines.push(`• Generation caps per period — ${joinList(caps)}.`);
      lines.push(`• Unused allowance ${rights.unused_allowance_behavior === 'rolls_over' ? `rolls over${rights.rollover_cap ? ` (cap ${rights.rollover_cap})` : ' (uncapped)'}` : 'expires'}.`);
    }
    const reuseMap = { new_generations_only: 'New generations only — republishing existing assets does not count toward the cap.', new_and_republishing: 'Both new generations and republishing count toward the monthly cap.' };
    lines.push(`• ${reuseMap[rights.content_reuse_scope] || ''}`);
    const subExcl = SUBSCRIPTION_EXCLUSIVITY.find(o => o.value === rights.subscription_exclusivity);
    if (subExcl) {
      let line = `• Subscription exclusivity: ${subExcl.label.toLowerCase()}`;
      if (rights.subscription_exclusivity === 'category_exclusive' && rights.subscription_exclusivity_categories?.length) {
        line += ` (${joinList(rights.subscription_exclusivity_categories)})`;
      }
      line += '.';
      lines.push(line);
    }
    if (rights.suspension_permitted) {
      lines.push(`• Agency may suspend up to ${rights.suspension_max_days || '0'} days per ${pretty(rights.suspension_period_basis)}; creator ${rights.suspension_fee_continues ? 'continues to receive' : 'does not receive'} fee during suspension.`);
    }
    const pcMap = { fixed: '• Fixed rate — fee cannot change without a new agreement.', agency_with_notice: `• Agency may adjust rate with ${rights.price_change_notice_days || '0'} days' notice before next period.`, mutually_agreed: '• Rate changes require both parties to agree in writing.' };
    if (pcMap[rights.price_change_policy]) lines.push(pcMap[rights.price_change_policy]);
    const feeMap = { full_period: 'full current period', prorated: 'prorated to cancellation date', none: 'no further fee' };
    lines.push(`• Agency cancellation: ${rights.agency_cancellation_notice_days || '0'} days' notice; fee on cancellation = ${feeMap[rights.agency_cancellation_fee] || pretty(rights.agency_cancellation_fee)}.`);
    lines.push(`• Creator withdrawal: ${rights.creator_withdrawal_notice_days || '0'} days' notice.`);
    const accumMap = {
      must_takedown: `Assets must be taken down within ${rights.accumulated_assets_days || '0'} days after subscription ends.`,
      remain_live: 'Already-published assets may remain live; no new generation.',
      remain_live_period: `Assets may remain live for ${rights.accumulated_assets_days || '0'} days after subscription ends, then must be removed.`,
    };
    lines.push(`• ${accumMap[rights.accumulated_assets_post_subscription] || ''}`);
  }

  // 4.6 Termination & Expiry
  if (rights?.termination_triggers?.length) {
    lines.push('');
    lines.push('Termination & Expiry:');
    const trigLabels = rights.termination_triggers.map(t => TERMINATION_TRIGGERS.find(x => x.value === t)?.label?.toLowerCase() || pretty(t));
    lines.push(`• Agency may terminate if: ${joinList(trigLabels)}.`);
    const cureMap = { none: 'No cure period.', '48h': '48-hour cure period.', '7d': '7-day cure period.', '14d': '14-day cure period.', '30d': '30-day cure period.' };
    if (cureMap[rights.cure_period]) lines.push(`• ${cureMap[rights.cure_period]}`);
  }
  if (rights?.morality_clause) {
    let line = '• Morality clause active';
    if (rights.morality_conditions?.length) {
      const condLabels = rights.morality_conditions.map(c => MORALITY_CONDITIONS.find(x => x.value === c)?.label?.toLowerCase() || pretty(c));
      line += `: triggered by ${joinList(condLabels)}`;
    }
    if (rights.morality_other) line += `; also: ${rights.morality_other}`;
    line += '.';
    lines.push(line);
    const consMap = {
      immediate: '• Trigger consequence: immediate termination, no fee owed for remaining term.',
      payment_for_work: '• Trigger consequence: termination with payment for work completed to date.',
      suspend_review: '• Trigger consequence: agency may suspend and review before terminating.',
    };
    if (consMap[rights.morality_consequence]) lines.push(consMap[rights.morality_consequence]);
  }
  if (rights?.early_termination_permitted) {
    let line = `• Agency may terminate without cause with ${rights.early_termination_notice_days || '0'} days' notice; compensation: `;
    const compMap = {
      full: 'full fee still owed',
      prorated: 'prorated to termination date',
      kill_fee: `kill fee of ${rights.early_termination_kill_fee_percentage || '0'}% of total agreed fee`,
      none: 'no further compensation owed',
    };
    line += compMap[rights.early_termination_compensation] || pretty(rights.early_termination_compensation);
    line += '.';
    lines.push(line);
  }
  if (rights?.post_termination_takedown_days || rights?.post_termination_deletion_days) {
    lines.push(`• Post-termination: takedown within ${rights.post_termination_takedown_days || '0'} days; deletion within ${rights.post_termination_deletion_days || '0'} days.`);
  }
  if (rights?.residual_use_permitted) {
    lines.push(`• Residual use of assets in physical/print production permitted for ${rights.residual_use_days || '0'} days, limited to: ${(rights.residual_use_channels || []).join(', ') || '—'}.`);
  }
  if (rights?.deletion_confirmation_required) {
    lines.push('• Written confirmation of asset deletion provided to creator.');
  }
  if (rights?.governing_law || rights?.dispute_resolution) {
    const gl = GOVERNING_LAW_OPTIONS.find(o => o.value === rights.governing_law)?.label || pretty(rights.governing_law);
    const dr = DISPUTE_RESOLUTION.find(o => o.value === rights.dispute_resolution)?.label || pretty(rights.dispute_resolution);
    if (rights.governing_law) lines.push(`• Governing law: ${gl}.`);
    if (rights.dispute_resolution) lines.push(`• Dispute resolution: ${dr}.`);
  }

  // Platform-level commitments
  lines.push('');
  lines.push('Platform commitments included with every proposal:');
  lines.push('• Generated assets and source materials will not be used to train, fine-tune, or improve any AI model beyond this campaign.');
  lines.push('• Creator retains full ownership of their likeness, name, voice, and persona at all times.');
  lines.push('• No sexual content, no content involving minors in sensitive contexts, no depiction of illegal activity.');

  return lines.join(' ').replace(/  +/g, ' ').trim();
}

// ═══════════════════════════════════════════════════════════
// SEND READINESS PANEL
// ═══════════════════════════════════════════════════════════

function SendReadinessPanel({ sections, onJump, governingLawSet }) {
  const required = sections.filter(s => s.required);
  const completeCount = required.filter(s => s.complete).length;
  const totalRequired = required.length;
  const ready = completeCount === totalRequired && governingLawSet;
  return (
    <div data-testid="send-readiness-panel" className="bg-white border border-slate-100 rounded-xl p-3 mb-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className={`w-7 h-7 rounded-full flex items-center justify-center ${ready ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
            {ready ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-800">{ready ? 'Ready to send' : `${completeCount}/${totalRequired} required sections complete`}</p>
            <p className="text-[10px] text-slate-400">{ready ? 'All required terms are set.' : 'Click any section to jump to it.'}</p>
          </div>
        </div>
        {!governingLawSet && (
          <Badge variant="outline" className="text-[9px] bg-red-50 text-red-600 border-red-200">
            Governing law not set
          </Badge>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {sections.map(s => (
          <button key={s.id} onClick={() => onJump(s.id)} type="button"
            data-testid={`jump-${s.id}`}
            className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors flex items-center gap-1
              ${s.complete ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : s.required ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-slate-50 text-slate-500 border-slate-200'}`}>
            {s.complete ? <CheckCircle className="w-2.5 h-2.5" /> : <AlertCircle className="w-2.5 h-2.5" />}
            {s.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// CONFIRMATION MODAL FOR SENSITIVE TOGGLES (Phase 2 stub)
// ═══════════════════════════════════════════════════════════

export function SensitiveToggleConfirm({ open, onOpenChange, label, onConfirm }) {
  const [typed, setTyped] = useState('');
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) setTyped(''); onOpenChange(o); }}>
      <DialogContent className="bg-white max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-700">
            <AlertTriangle className="w-4 h-4" /> Confirm Sensitive Choice
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Enabling <span className="font-medium">{label}</span> is a deliberate, sensitive choice. Type ENABLE to confirm.
          </DialogDescription>
        </DialogHeader>
        <Input value={typed} onChange={e => setTyped(e.target.value)} placeholder="Type ENABLE" className="h-9" />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button size="sm" disabled={typed !== 'ENABLE'} onClick={() => { onConfirm(); onOpenChange(false); setTyped(''); }}
            className="bg-amber-600 hover:bg-amber-700 text-white">Enable</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════

export function ProposalTab({ deal, onRefresh, campaign }) {
  const proposal = deal.proposal || {};
  const isDraft = deal.proposal_status === 'draft' || deal.proposal_status === 'none' || !deal.proposal_status;
  const canSendNew = isDraft || deal.proposal_status === 'rejected' || deal.proposal_status === 'countered';
  // Agency does NOT accept/reject their own proposal — only the creator can. canRespond removed.

  const [editing, setEditing] = useState(isDraft);
  const [saving, setSaving] = useState(false);
  const [openSections, setOpenSections] = useState({
    brief: true, offer: true, likeness: false,
    license_scope: false, ai_generation: false, permitted_use: false, ownership: false, subscription: false, termination: false, extensions: false,
  });
  const toggle = (s) => setOpenSections(prev => ({ ...prev, [s]: !prev[s] }));
  const jumpTo = (id) => {
    setOpenSections(prev => ({ ...prev, [id]: true }));
    setTimeout(() => {
      const el = document.getElementById(`section-${id}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  };

  // ── Form state
  const rt = proposal.rights_terms || {};
  const co = proposal.commercial_offer || {};
  const lr = proposal.likeness_request || {};

  const [brief, setBrief] = useState({
    description: proposal.campaign_brief?.description || campaign?.description || '',
    objective: proposal.campaign_brief?.objective || campaign?.objective || '',
    deliverable_types: proposal.campaign_brief?.deliverable_types || campaign?.deliverable_types || [],
    channels: proposal.campaign_brief?.channels || campaign?.channels || [],
    references: proposal.campaign_brief?.references || [],
  });
  const [offer, setOffer] = useState({
    deal_structure: co.deal_structure || 'one_time',
    base_amount: co.base_amount ?? co.amount ?? '',
    recurring_amount: co.recurring_amount ?? '',
    signon_bonus_amount: co.signon_bonus_amount ?? '',
    signon_bonus_currency: co.signon_bonus_currency || 'USD',
    signon_bonus_timing: co.signon_bonus_timing || 'on_signing',
    currency: co.currency || 'USD',
    payment_model: co.payment_model || 'one_off',
    response_deadline: co.response_deadline || '',
    notes: co.notes || proposal.notes || '',
  });
  const [rights, setRights] = useState({
    exclusivity: rt.exclusivity || 'non_exclusive',
    exclusivity_categories: rt.exclusivity_categories || [],
    territory: rt.territory || campaign?.territory || [],
    duration_type: rt.duration_type || 'campaign_locked',
    rolling_interval: rt.rolling_interval || '',
    cancellation_notice_days: rt.cancellation_notice_days || 0,
    term_start: rt.term_start || campaign?.start_date || '',
    term_end: rt.term_end || campaign?.end_date || '',
    expiry_behavior: rt.expiry_behavior || 'revert',
    sublicensing: rt.sublicensing || 'not_permitted',
    purpose_of_use: rt.purpose_of_use || [],
    usage_scope: rt.usage_scope || 'single_campaign',
    content_restrictions: rt.content_restrictions || [
      // Locked-on always; agency may deselect optional defaults
      ...CONTENT_RESTRICTIONS_LOCKED.map(o => o.value),
      'political', 'religious', 'violence', 'competitor_brands', 'alcohol_tobacco',
      'pharmaceutical', 'financial', 'gambling', 'personal_views', 'satire_parody', 'news_editorial',
    ],
    competitor_restriction: rt.competitor_restriction || false,
    competitor_names: rt.competitor_names || '',
    output_reuse: rt.output_reuse || 'not_permitted',
    source_material_reuse: rt.source_material_reuse || 'not_permitted',
    asset_ownership: rt.asset_ownership || 'agency_owns_creator_portfolio',
    creator_portfolio_rights: rt.creator_portfolio_rights ?? true,
    moral_rights: rt.moral_rights || 'fully_reserved',
    endorsement_requested: rt.endorsement_requested || false,
    contract_url: rt.contract_url || '',
    // ── 4.2 AI Generation Permissions ──
    generation_types: rt.generation_types || [],
    asset_volume: rt.asset_volume || {},  // { [type]: { unlimited: bool, cap: number } }
    visual_representation: rt.visual_representation || 'realistic',
    permitted_alterations: rt.permitted_alterations || [],
    dialogue_permitted: rt.dialogue_permitted || false,
    dialogue_scripted_only: rt.dialogue_scripted_only ?? true,
    statements_permitted: rt.statements_permitted || false,
    statement_scope: rt.statement_scope || 'agreed_script',
    statement_scope_other: rt.statement_scope_other || '',
    post_generation_modifications: rt.post_generation_modifications || [],
    ai_model_data_usage_prohibited: true,  // locked platform commitment
    // ── 4.5 Subscription License Terms (only used when deal_structure is subscription/hybrid) ──
    subscription_billing_frequency: rt.subscription_billing_frequency || 'monthly',
    generation_caps_enabled: rt.generation_caps_enabled || false,
    generation_caps: rt.generation_caps || {},  // { [type]: { unlimited: bool, cap: number } }
    unused_allowance_behavior: rt.unused_allowance_behavior || 'expires',
    rollover_cap: rt.rollover_cap ?? '',
    content_reuse_scope: rt.content_reuse_scope || 'new_generations_only',
    subscription_exclusivity: rt.subscription_exclusivity || 'non_exclusive',
    subscription_exclusivity_categories: rt.subscription_exclusivity_categories || [],
    suspension_permitted: rt.suspension_permitted || false,
    suspension_max_days: rt.suspension_max_days ?? '',
    suspension_period_basis: rt.suspension_period_basis || 'billing_period',  // billing_period | year
    suspension_fee_continues: rt.suspension_fee_continues ?? false,
    price_change_policy: rt.price_change_policy || 'fixed',
    price_change_notice_days: rt.price_change_notice_days ?? '',
    agency_cancellation_notice_days: rt.agency_cancellation_notice_days ?? '',
    agency_cancellation_fee: rt.agency_cancellation_fee || 'prorated',
    creator_withdrawal_notice_days: rt.creator_withdrawal_notice_days ?? '',
    accumulated_assets_post_subscription: rt.accumulated_assets_post_subscription || 'must_takedown',
    accumulated_assets_days: rt.accumulated_assets_days ?? '',
    // ── 4.6 Termination & Expiry ──
    termination_triggers: rt.termination_triggers || [],
    cure_period: rt.cure_period || '7d',
    morality_clause: rt.morality_clause ?? true,
    morality_conditions: rt.morality_conditions || [],
    morality_other: rt.morality_other || '',
    morality_consequence: rt.morality_consequence || 'suspend_review',
    early_termination_permitted: rt.early_termination_permitted || false,
    early_termination_notice_days: rt.early_termination_notice_days ?? '',
    early_termination_compensation: rt.early_termination_compensation || 'prorated',
    early_termination_kill_fee_percentage: rt.early_termination_kill_fee_percentage ?? '',
    post_termination_takedown_days: rt.post_termination_takedown_days ?? 14,
    post_termination_deletion_days: rt.post_termination_deletion_days ?? 30,
    residual_use_permitted: rt.residual_use_permitted || false,
    residual_use_days: rt.residual_use_days ?? '',
    residual_use_channels: rt.residual_use_channels || [],
    deletion_confirmation_required: rt.deletion_confirmation_required ?? true,
    governing_law: rt.governing_law || '',  // empty = inherit from agency default
    dispute_resolution: rt.dispute_resolution || '',  // empty = inherit from agency default
  });
  const [likenessReq, setLikenessReq] = useState({
    standard_pack_included: lr.standard_pack_included ?? true,
    additional_requirements: lr.additional_requirements || [],
    custom_notes: lr.custom_notes || '',
  });

  // Campaign-level extensions
  const [pricingMethod, setPricingMethod] = useState(campaign?.pricing_method || 'fixed_per_submission');
  const [continuationAllowed, setContinuationAllowed] = useState(campaign?.continuation_allowed || false);
  const [royaltyEnabled, setRoyaltyEnabled] = useState(campaign?.royalty_enabled || false);
  const [royaltyTerms, setRoyaltyTerms] = useState(campaign?.royalty_terms || {
    royalty_type: 'revenue_share', royalty_rate: '', calculation_basis: '', notes: '',
  });

  // Agency settings (for governing law gate + presets)
  const [agencyGovLaw, setAgencyGovLaw] = useState(null);
  const [agencyDispute, setAgencyDispute] = useState(null);
  const [presets, setPresets] = useState([]);
  const [presetMenuOpen, setPresetMenuOpen] = useState(false);
  const [regenSummary, setRegenSummary] = useState(false);

  const regenerateAiSummary = async () => {
    setRegenSummary(true);
    try {
      await API.post(`/buyer/deals/${deal.id}/proposal/ai-summary`);
      toast.success('Summary refreshed with AI');
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not generate AI summary');
    } finally { setRegenSummary(false); }
  };

  const [pendingDeliberateAlt, setPendingDeliberateAlt] = useState(null);  // { value, label } for ENABLE confirm
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, p] = await Promise.all([
          API.get('/buyer/settings'),
          API.get('/buyer/proposals/presets'),
        ]);
        if (cancelled) return;
        setAgencyGovLaw(s.data?.settings?.proposal_defaults?.default_governing_law || '');
        setAgencyDispute(s.data?.settings?.proposal_defaults?.default_dispute_resolution || '');
        setPresets(p.data?.presets || []);
      } catch { /* non-blocking */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Section completion logic
  const briefComplete = !!(brief.description && brief.objective && brief.deliverable_types.length && brief.channels.length);
  const offerComplete = useMemo(() => {
    if (!offer.deal_structure) return false;
    if (offer.deal_structure === 'one_time') return Number(offer.base_amount) > 0;
    if (offer.deal_structure === 'subscription') return Number(offer.recurring_amount) > 0;
    if (offer.deal_structure === 'hybrid') return Number(offer.base_amount) > 0 && Number(offer.recurring_amount) > 0;
    return false;
  }, [offer]);
  const licenseScopeComplete = !!(rights.exclusivity && rights.territory.length && rights.duration_type
    && rights.term_start && rights.term_end && rights.expiry_behavior && rights.sublicensing
    && (rights.exclusivity !== 'category_exclusive' || rights.exclusivity_categories.length));
  const permittedUseComplete = !!(rights.purpose_of_use.length && rights.usage_scope
    && rights.content_restrictions.length && rights.output_reuse && rights.source_material_reuse);
  const ownershipComplete = !!(rights.asset_ownership && rights.moral_rights);
  const aiGenerationComplete = !!(rights.generation_types.length && rights.visual_representation);

  // ── Section 4.5 visibility & completion (subscription/hybrid only) ──
  const subscriptionActive = offer.deal_structure === 'subscription' || offer.deal_structure === 'hybrid';
  const subscriptionComplete = !subscriptionActive ? true : !!(
    rights.subscription_billing_frequency
    && rights.content_reuse_scope
    && rights.subscription_exclusivity
    && (rights.subscription_exclusivity !== 'category_exclusive' || rights.subscription_exclusivity_categories.length)
    && rights.price_change_policy
    && (rights.price_change_policy !== 'agency_with_notice' || Number(rights.price_change_notice_days) > 0)
    && Number(rights.agency_cancellation_notice_days) > 0
    && rights.agency_cancellation_fee
    && Number(rights.creator_withdrawal_notice_days) > 0
    && rights.accumulated_assets_post_subscription
    && (rights.accumulated_assets_post_subscription === 'remain_live' || Number(rights.accumulated_assets_days) > 0)
  );

  // ── Section 4.6 completion ──
  const govLawEffective = rights.governing_law || agencyGovLaw || '';
  const terminationComplete = !!(
    rights.termination_triggers.length
    && rights.cure_period
    && (!rights.morality_clause || rights.morality_conditions.length || rights.morality_other)
    && (!rights.early_termination_permitted || (Number(rights.early_termination_notice_days) > 0
        && rights.early_termination_compensation
        && (rights.early_termination_compensation !== 'kill_fee' || Number(rights.early_termination_kill_fee_percentage) > 0)))
    && Number(rights.post_termination_takedown_days) > 0
    && Number(rights.post_termination_deletion_days) > 0
    && (!rights.residual_use_permitted || (Number(rights.residual_use_days) > 0 && rights.residual_use_channels.length))
    && govLawEffective
    && (rights.dispute_resolution || agencyDispute) // dispute_resolution falls back to agency default
  );

  const likenessComplete = true;

  const sections = [
    { id: 'brief', label: 'Campaign Brief', complete: briefComplete, required: true },
    { id: 'offer', label: 'Commercial Offer', complete: offerComplete, required: true },
    { id: 'license_scope', label: 'License Scope', complete: licenseScopeComplete, required: true },
    { id: 'ai_generation', label: 'AI Generation', complete: aiGenerationComplete, required: true },
    { id: 'permitted_use', label: 'Permitted Use', complete: permittedUseComplete, required: true },
    { id: 'ownership', label: 'Ownership', complete: ownershipComplete, required: true },
    ...(subscriptionActive ? [{ id: 'subscription', label: 'Subscription', complete: subscriptionComplete, required: true }] : []),
    { id: 'termination', label: 'Termination', complete: terminationComplete, required: true },
    { id: 'likeness', label: 'Likeness Pack', complete: likenessComplete, required: false },
    { id: 'extensions', label: 'Extensions', complete: true, required: false },
  ];
  const allRequiredComplete = sections.every(s => !s.required || s.complete);

  // Conflict detection (4.5 vs 4.1 — for now just sublicensing & duration sanity)
  const conflicts = [];
  if (rights.duration_type === 'perpetual' && rights.expiry_behavior === 'auto_renew') {
    conflicts.push('Perpetual duration with auto-renew expiry is contradictory.');
  }
  if (rights.expiry_behavior === 'auto_renew' && rights.duration_type !== 'rolling') {
    conflicts.push('Auto-renew expiry only applies to Rolling duration.');
  }
  if (rights.duration_type === 'rolling' && !rights.rolling_interval) {
    conflicts.push('Rolling duration requires a renewal interval.');
  }

  // Moral-rights jurisdiction lock
  const govLaw = agencyGovLaw || '';
  const moralRightsStrong = MORAL_RIGHTS_STRONG.some(j => govLaw.toUpperCase().startsWith(j));

  // ── Apply preset
  const applyPreset = (preset) => {
    setOffer(o => ({ ...o, ...preset.commercial_offer }));
    setRights(r => ({ ...r, ...preset.rights_terms }));
    toast.success(`Preset applied: ${preset.name}`);
    setPresetMenuOpen(false);
  };

  // ── Save proposal
  const saveProposal = async (action) => {
    if (action === 'send' || action === 'resubmit') {
      if (!agencyGovLaw) {
        toast.error('Set governing law in Settings → Proposal Defaults before sending.');
        return;
      }
      if (!allRequiredComplete) {
        toast.error('Complete all required sections before sending.');
        return;
      }
    }
    setSaving(true);
    try {
      const summary = generatePlainSummary({ brief, offer, rights });
      const rightsPayload = {
        ...rights,
        // Inherit agency defaults if not overridden per-proposal
        governing_law: rights.governing_law || agencyGovLaw || '',
        dispute_resolution: rights.dispute_resolution || agencyDispute || '',
        plain_language_summary: summary,
        // Legacy mirrors
        license_type: rights.exclusivity === 'non_exclusive' ? 'non_exclusive' : 'exclusive',
        platforms: brief.channels,
        approval_required: true,
      };
      const offerPayload = {
        ...offer,
        base_amount: parseFloat(offer.base_amount) || 0,
        recurring_amount: parseFloat(offer.recurring_amount) || 0,
        signon_bonus_amount: parseFloat(offer.signon_bonus_amount) || 0,
        amount: parseFloat(offer.base_amount) || parseFloat(offer.recurring_amount) || 0,
      };
      await API.put(`/buyer/deals/${deal.id}/proposal/save`, {
        campaign_brief: brief,
        commercial_offer: offerPayload,
        rights_terms: rightsPayload,
        likeness_request: likenessReq,
        action,
      });
      // Save campaign-level extensions
      if (campaign?.id) {
        await API.put(`/buyer/campaigns/${campaign.id}`, {
          pricing_method: pricingMethod,
          continuation_allowed: continuationAllowed,
          royalty_enabled: royaltyEnabled,
          royalty_terms: royaltyEnabled ? royaltyTerms : null,
        });
      }
      toast.success(action === 'send' ? 'Proposal sent' : action === 'resubmit' ? 'Proposal resubmitted' : 'Draft saved');
      if (action === 'send' || action === 'resubmit') setEditing(false);
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const respondToProposal = async (action) => {
    try {
      await API.put(`/buyer/deals/${deal.id}/proposal/respond`, { action });
      toast.success(action === 'accept' ? 'Proposal accepted! License created.' : 'Proposal rejected.');
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // ═══════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════
  return (
    <div className="space-y-3" data-testid="proposal-tab">
      {/* Governing law gate */}
      {agencyGovLaw === '' && (
        <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 flex items-center gap-2" data-testid="gov-law-warning">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          <p className="text-xs text-amber-800 flex-1">
            Governing law must be set in <strong>Settings → Proposal Defaults</strong> before you can send any proposals.
          </p>
        </div>
      )}

      {/* ═══ READ-ONLY VIEW ═══ */}
      {!editing && (proposal.commercial_offer || proposal.offered_price) && (
        <div className="space-y-3">
          {/* Plain-language summary card */}
          <div className="bg-white rounded-xl border border-slate-100 p-5" data-testid="proposal-summary-card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                <FileText className="w-4 h-4 text-blue-500" /> Proposal Summary
                {rt.ai_summary && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-medium text-violet-600 bg-violet-50 border border-violet-100 px-1.5 py-0.5 rounded-full" data-testid="ai-summary-badge">
                    <Sparkles className="w-3 h-3" /> AI
                  </span>
                )}
              </h3>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={regenerateAiSummary} disabled={regenSummary}
                  className="text-xs h-7 text-violet-600 hover:bg-violet-50" data-testid="regenerate-ai-summary-btn">
                  {regenSummary ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                  {rt.ai_summary ? 'Regenerate' : 'Generate with AI'}
                </Button>
                <StatusBadge status={deal.proposal_status} />
              </div>
            </div>
            <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-line">
              {rt.ai_summary || rt.plain_language_summary || generatePlainSummary({ brief, offer, rights })}
            </p>
            <ViewFullTermsExpander rights={rt} offer={co} brief={proposal.campaign_brief || brief} />
          </div>

          {/* Counter History */}
          {deal.counter_history?.length > 0 && (
            <div className="bg-white rounded-xl border border-purple-100 p-4" data-testid="counter-history-section">
              <div className="flex items-center gap-2 mb-3">
                <MessageSquare className="w-4 h-4 text-purple-600" />
                <h3 className="text-sm font-semibold text-slate-800">Creator Counter Offers</h3>
                <span className="text-[10px] text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full border border-purple-100">{deal.counter_history.length}</span>
              </div>
              <div className="space-y-2">
                {deal.counter_history.map((c, i) => (
                  <div key={i} className="p-3 bg-purple-50/50 border border-purple-100 rounded-lg" data-testid={`counter-item-${i}`}>
                    <div className="flex justify-between items-start mb-1.5">
                      <span className="text-xs font-semibold text-purple-700">Counter #{i + 1}</span>
                      <span className="text-[10px] text-slate-500">{c.countered_at ? new Date(c.countered_at).toLocaleString() : ''}</span>
                    </div>
                    {c.counter_price ? <p className="text-sm font-semibold text-slate-900 mb-1">Proposed price: ${Number(c.counter_price).toLocaleString()}</p> : null}
                    {c.message ? (
                      <div className="mt-1.5 p-2 bg-white rounded border border-purple-100">
                        <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5">Creator's message</p>
                        <p className="text-xs text-slate-700 whitespace-pre-wrap">{c.message}</p>
                      </div>
                    ) : (
                      <p className="text-[11px] text-slate-400 italic">No message provided.</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action buttons — Agency does NOT accept/reject their own proposal; that's the creator's job. */}
          {deal.proposal_status === 'sent' && (
            <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 flex items-center gap-2" data-testid="awaiting-creator-banner">
              <Clock className="w-4 h-4 text-blue-600" />
              <p className="text-xs text-blue-800 flex-1">Awaiting creator's response. You can still edit the proposal until they accept, counter, or decline.</p>
            </div>
          )}
          {(canSendNew || deal.proposal_status === 'sent') && (
            <Button variant="outline" onClick={() => setEditing(true)} className="text-xs h-8 border-slate-200">
              <Edit3 className="w-3 h-3 mr-1" />{deal.proposal_status === 'sent' ? 'Edit Proposal' : 'Revise Proposal'}
            </Button>
          )}
        </div>
      )}

      {/* ═══ EMPTY STATE ═══ */}
      {!editing && !proposal.commercial_offer && !proposal.offered_price && (
        <div className="text-center py-10 bg-white rounded-xl border border-slate-100">
          <Send className="w-8 h-8 text-slate-200 mx-auto mb-2" />
          <p className="text-sm text-slate-400">No proposal yet</p>
          <Button size="sm" className="mt-3 bg-blue-600 hover:bg-blue-700 text-white text-xs"
            onClick={() => setEditing(true)} data-testid="create-proposal-btn">
            <Send className="w-3 h-3 mr-1" /> Create Proposal
          </Button>
        </div>
      )}

      {/* ═══ EDITING / BUILDING ═══ */}
      {editing && (
        <div className="space-y-3">
          {/* Send Readiness */}
          <SendReadinessPanel sections={sections} onJump={jumpTo} governingLawSet={!!agencyGovLaw} />

          {/* Conflicts */}
          {conflicts.length > 0 && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200" data-testid="conflict-warnings">
              <p className="text-xs font-semibold text-red-700 mb-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Resolve conflicts</p>
              <ul className="text-[11px] text-red-600 list-disc list-inside space-y-0.5">
                {conflicts.map((c, i) => <li key={i}>{c}</li>)}
              </ul>
            </div>
          )}

          {/* Preset apply */}
          <div className="flex items-center justify-between bg-white rounded-xl border border-slate-100 p-3">
            <div className="flex items-center gap-2">
              <Bookmark className="w-4 h-4 text-slate-400" />
              <p className="text-xs text-slate-600">Start from a preset to fill defaults.</p>
            </div>
            <Button size="sm" variant="outline" data-testid="apply-preset-btn" onClick={() => setPresetMenuOpen(true)}
              className="text-xs h-7 border-slate-200">
              Apply Preset
            </Button>
          </div>

          {/* ── Section 1: Campaign Brief ── */}
          <SectionToggle id="brief" title="Campaign Brief" icon={FileText} open={openSections.brief} onToggle={() => toggle('brief')}
            complete={briefComplete} required>
            {(campaign?.description || campaign?.objective || campaign?.channels?.length) && (
              <PreFilledBanner message="Some fields pre-filled from the campaign — edit freely; changes do not affect other creators." />
            )}
            <div className="space-y-3 mt-2">
              <div>
                <Label className="text-xs text-slate-600 mb-1 block">Description *</Label>
                <Textarea data-testid="brief-description" value={brief.description}
                  onChange={e => setBrief(p => ({ ...p, description: e.target.value }))}
                  rows={2} placeholder="What is this campaign about?" className="text-xs border-slate-200" />
              </div>
              <div>
                <Label className="text-xs text-slate-600 mb-1 block">Objective *</Label>
                <Input data-testid="brief-objective" value={brief.objective}
                  onChange={e => setBrief(p => ({ ...p, objective: e.target.value }))}
                  placeholder="What is the goal of this campaign?" className="h-8 text-xs border-slate-200" />
              </div>
              <ChipSelect testid="brief-deliverables" label="Deliverable Types *" options={DELIVERABLE_TYPES} selected={brief.deliverable_types}
                onChange={v => setBrief(p => ({ ...p, deliverable_types: v }))} />
              <ChipSelect testid="brief-channels" label="Channels *" options={PLATS} selected={brief.channels}
                onChange={v => setBrief(p => ({ ...p, channels: v }))} />
              <ReferencesEditor refs={brief.references} onChange={(v) => setBrief(p => ({ ...p, references: v }))} />
            </div>
          </SectionToggle>

          {/* ── Section 2: Commercial Offer ── */}
          <SectionToggle id="offer" title="Commercial Offer" icon={DollarSign} open={openSections.offer} onToggle={() => toggle('offer')}
            complete={offerComplete} required
            badge={<Badge variant="outline" className="text-[9px] ml-1">{DEAL_STRUCTURES.find(d => d.value === offer.deal_structure)?.label || ''}</Badge>}>
            <div className="space-y-3 mt-2">
              <div>
                <Label className="text-xs text-slate-600 mb-1.5 block">Deal Structure *</Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {DEAL_STRUCTURES.map(d => (
                    <button key={d.value} type="button" data-testid={`deal-structure-${d.value}`}
                      onClick={() => setOffer(o => ({ ...o, deal_structure: d.value, payment_model: d.value === 'subscription' ? 'recurring' : 'one_off' }))}
                      className={`text-left p-3 rounded-lg border transition-all
                        ${offer.deal_structure === d.value ? 'bg-blue-50 border-blue-300' : 'bg-white border-slate-200 hover:border-blue-200'}`}>
                      <p className="text-xs font-semibold text-slate-800">{d.label}</p>
                      <p className="text-[10px] text-slate-500 mt-0.5">{d.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {(offer.deal_structure === 'one_time' || offer.deal_structure === 'hybrid') && (
                  <div>
                    <Label className="text-xs text-slate-600 mb-1 block">
                      {offer.deal_structure === 'hybrid' ? 'Base Offer Amount *' : 'Total Offer Amount *'}
                    </Label>
                    <Input data-testid="offer-base-amount" type="number" value={offer.base_amount}
                      onChange={e => setOffer(o => ({ ...o, base_amount: e.target.value }))}
                      placeholder="5000" className="h-8 text-xs border-slate-200" />
                  </div>
                )}
                {(offer.deal_structure === 'subscription' || offer.deal_structure === 'hybrid') && (
                  <div>
                    <Label className="text-xs text-slate-600 mb-1 block">Monthly / Recurring Fee *</Label>
                    <Input data-testid="offer-recurring-amount" type="number" value={offer.recurring_amount}
                      onChange={e => setOffer(o => ({ ...o, recurring_amount: e.target.value }))}
                      placeholder="2000" className="h-8 text-xs border-slate-200" />
                  </div>
                )}
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">Currency</Label>
                  <Select value={offer.currency} onValueChange={v => setOffer(o => ({ ...o, currency: v }))}>
                    <SelectTrigger className="h-8 text-xs border-slate-200"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {['USD', 'GBP', 'EUR', 'CAD', 'AUD'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">Payment Model</Label>
                  <Select value={offer.payment_model} onValueChange={v => setOffer(o => ({ ...o, payment_model: v }))}
                    disabled={offer.deal_structure === 'subscription'}>
                    <SelectTrigger className="h-8 text-xs border-slate-200"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="one_off">One-Off</SelectItem>
                      <SelectItem value="recurring">Recurring</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Sign-on bonus (subscription / hybrid) */}
              {(offer.deal_structure === 'subscription' || offer.deal_structure === 'hybrid') && (
                <div className="p-3 rounded-lg bg-emerald-50/40 border border-emerald-100">
                  <label className="flex items-center gap-2 text-xs text-slate-700 mb-2">
                    <input type="checkbox" data-testid="signon-bonus-toggle"
                      checked={Number(offer.signon_bonus_amount) > 0}
                      onChange={e => setOffer(o => ({ ...o, signon_bonus_amount: e.target.checked ? '500' : '' }))}
                      className="rounded border-slate-300" />
                    Include a one-time sign-on bonus
                  </label>
                  {Number(offer.signon_bonus_amount) > 0 && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-[10px] text-slate-500 mb-1 block">Bonus Amount</Label>
                        <Input data-testid="signon-bonus-amount" type="number" value={offer.signon_bonus_amount}
                          onChange={e => setOffer(o => ({ ...o, signon_bonus_amount: e.target.value }))}
                          className="h-8 text-xs border-slate-200" />
                      </div>
                      <div>
                        <Label className="text-[10px] text-slate-500 mb-1 block">Paid</Label>
                        <Select value={offer.signon_bonus_timing} onValueChange={v => setOffer(o => ({ ...o, signon_bonus_timing: v }))}>
                          <SelectTrigger data-testid="signon-bonus-timing" className="h-8 text-xs border-slate-200"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-white">
                            {SIGNON_TIMINGS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">Response Deadline</Label>
                  <Input type="date" value={offer.response_deadline}
                    onChange={e => setOffer(o => ({ ...o, response_deadline: e.target.value }))}
                    className="h-8 text-xs border-slate-200" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-600 mb-1 block">Offer Notes</Label>
                <Textarea value={offer.notes} onChange={e => setOffer(o => ({ ...o, notes: e.target.value }))}
                  rows={2} placeholder="Any additional context about this offer" className="text-xs border-slate-200" />
              </div>
            </div>
          </SectionToggle>

          {/* ── Section 3: Likeness Pack Request ── */}
          <SectionToggle id="likeness" title="Likeness Pack Request" icon={Package} open={openSections.likeness} onToggle={() => toggle('likeness')}
            complete={likenessComplete}>
            <div className="space-y-3 mt-2">
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-3.5 h-3.5 text-blue-600" />
                  <span className="text-xs font-semibold text-blue-800">Standard Pack Included</span>
                </div>
                <ul className="space-y-1 text-[11px] text-blue-700 ml-1">
                  <li>• Front and profile images</li>
                  <li>• Smiling and neutral expressions</li>
                  <li>• Full body reference</li>
                  <li>• Audio clip</li>
                  <li>• Talking head video</li>
                  <li>• Basic notes</li>
                </ul>
              </div>
              <div>
                <Label className="text-xs text-slate-600 mb-1.5 block">Additional Requirements</Label>
                <div className="space-y-1.5">
                  {EXTRA_LIKENESS_OPTIONS.map(opt => (
                    <label key={opt.value} className="flex items-center gap-2 text-xs text-slate-700">
                      <input type="checkbox" checked={likenessReq.additional_requirements.includes(opt.value)}
                        onChange={e => setLikenessReq(p => ({ ...p, additional_requirements: e.target.checked
                          ? [...p.additional_requirements, opt.value]
                          : p.additional_requirements.filter(x => x !== opt.value) }))}
                        className="rounded border-slate-300" />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-600 mb-1 block">Custom Notes</Label>
                <Textarea value={likenessReq.custom_notes}
                  onChange={e => setLikenessReq(p => ({ ...p, custom_notes: e.target.value }))}
                  rows={2} placeholder="Any specific instructions for this creator's pack" className="text-xs border-slate-200" />
              </div>
            </div>
          </SectionToggle>

          {/* ── Section 4.1: License Scope ── */}
          <SectionToggle id="license_scope" title="License Scope" icon={Shield} open={openSections.license_scope} onToggle={() => toggle('license_scope')}
            complete={licenseScopeComplete} required
            badge={<Badge variant="outline" className="text-[9px] ml-1 capitalize">{rights.exclusivity?.replace(/_/g, ' ')}</Badge>}>
            <div className="space-y-3 mt-2">
              <div>
                <Label className="text-xs text-slate-600 mb-1.5 block">Exclusivity *</Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {EXCLUSIVITY_OPTIONS.map(o => (
                    <button key={o.value} type="button" data-testid={`exclusivity-${o.value}`}
                      onClick={() => setRights(r => ({ ...r, exclusivity: o.value }))}
                      className={`text-left p-2.5 rounded-lg border transition-all
                        ${rights.exclusivity === o.value ? 'bg-blue-50 border-blue-300' : 'bg-white border-slate-200 hover:border-blue-200'}`}>
                      <p className="text-xs font-semibold text-slate-800">{o.label}</p>
                      <p className="text-[10px] text-slate-500 mt-0.5">{o.desc}</p>
                    </button>
                  ))}
                </div>
                {rights.exclusivity === 'category_exclusive' && (
                  <div className="mt-2">
                    <ChipSelect testid="exclusivity-categories" label="Exclusive within these categories *" options={EXCLUSIVITY_CATEGORIES}
                      selected={rights.exclusivity_categories}
                      onChange={v => setRights(r => ({ ...r, exclusivity_categories: v }))} />
                  </div>
                )}
              </div>

              <ChipSelect testid="rights-territory" label="Territory *" options={TERRS} selected={rights.territory}
                onChange={(v) => {
                  // Global deselects all others
                  if (v.includes('Global') && !rights.territory.includes('Global')) {
                    setRights(r => ({ ...r, territory: ['Global'] }));
                  } else if (rights.territory.includes('Global') && v.length > 1) {
                    setRights(r => ({ ...r, territory: v.filter(x => x !== 'Global') }));
                  } else {
                    setRights(r => ({ ...r, territory: v }));
                  }
                }} />

              <div>
                <Label className="text-xs text-slate-600 mb-1.5 block">License Duration *</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {DURATION_TYPES.map(d => (
                    <button key={d.value} type="button" data-testid={`duration-${d.value}`}
                      onClick={() => setRights(r => ({ ...r, duration_type: d.value }))}
                      className={`text-left p-2 rounded-lg border transition-all
                        ${rights.duration_type === d.value ? 'bg-blue-50 border-blue-300' : 'bg-white border-slate-200 hover:border-blue-200'}`}>
                      <p className="text-[11px] font-semibold text-slate-800">{d.label}</p>
                      <p className="text-[9px] text-slate-500 mt-0.5">{d.desc}</p>
                    </button>
                  ))}
                </div>
                {rights.duration_type === 'rolling' && (
                  <div className="mt-2 grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[10px] text-slate-500 mb-1 block">Renewal Interval</Label>
                      <Select value={rights.rolling_interval} onValueChange={v => setRights(r => ({ ...r, rolling_interval: v }))}>
                        <SelectTrigger className="h-8 text-xs border-slate-200"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent className="bg-white">
                          {['monthly', 'quarterly', 'annually'].map(i => <SelectItem key={i} value={i} className="capitalize">{i}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[10px] text-slate-500 mb-1 block">Cancellation Notice (days)</Label>
                      <Input type="number" value={rights.cancellation_notice_days}
                        onChange={e => setRights(r => ({ ...r, cancellation_notice_days: parseInt(e.target.value) || 0 }))}
                        className="h-8 text-xs border-slate-200" />
                    </div>
                  </div>
                )}
                {rights.duration_type === 'perpetual' && (
                  <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md">
                    <p className="text-[10px] text-amber-700">⚠ Perpetual licenses are uncommon and may significantly affect creator willingness to accept.</p>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">Term Start *</Label>
                  <Input data-testid="rights-term-start" type="date" value={rights.term_start}
                    onChange={e => setRights(r => ({ ...r, term_start: e.target.value }))}
                    className="h-8 text-xs border-slate-200" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">Term End *</Label>
                  <Input data-testid="rights-term-end" type="date" value={rights.term_end}
                    onChange={e => setRights(r => ({ ...r, term_end: e.target.value }))}
                    className="h-8 text-xs border-slate-200" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">Expiry Behavior *</Label>
                  <Select value={rights.expiry_behavior} onValueChange={v => setRights(r => ({ ...r, expiry_behavior: v }))}>
                    <SelectTrigger data-testid="expiry-behavior" className="h-8 text-xs border-slate-200"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {EXPIRY_BEHAVIORS.map(o => <SelectItem key={o.value} value={o.value}
                        disabled={o.value === 'auto_renew' && rights.duration_type !== 'rolling'}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">Sublicensing *</Label>
                  <Select value={rights.sublicensing} onValueChange={v => setRights(r => ({ ...r, sublicensing: v }))}>
                    <SelectTrigger data-testid="sublicensing" className="h-8 text-xs border-slate-200"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {SUBLICENSING_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </SectionToggle>

          {/* ── Section 4.2: AI Generation Permissions ── */}
          <SectionToggle id="ai_generation" title="AI Generation Permissions" icon={Zap} open={openSections.ai_generation} onToggle={() => toggle('ai_generation')}
            complete={aiGenerationComplete} required
            badge={<Badge variant="outline" className="text-[9px] ml-1">{rights.generation_types.length} type{rights.generation_types.length === 1 ? '' : 's'}</Badge>}>
            <div className="space-y-4 mt-2">
              {/* Generation Types */}
              <div>
                <Label className="text-xs text-slate-600 mb-1.5 block">Generation Types Permitted *</Label>
                <p className="text-[10px] text-slate-400 mb-2">Select all generation types the agency is permitted to create from the creator's likeness.</p>
                <div className="flex flex-wrap gap-1.5">
                  {GENERATION_TYPES.map(g => {
                    const active = rights.generation_types.includes(g.value);
                    return (
                      <button key={g.value} type="button" data-testid={`gen-type-${g.value}`}
                        onClick={() => setRights(r => ({
                          ...r,
                          generation_types: active ? r.generation_types.filter(x => x !== g.value) : [...r.generation_types, g.value],
                        }))}
                        className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-all
                          ${active ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-500 border-slate-200 hover:border-blue-300'}`}>
                        {g.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Asset Volume per type */}
              {rights.generation_types.length > 0 && (
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                  <Label className="text-xs text-slate-700 font-semibold mb-2 block">Asset Volume per Type</Label>
                  <p className="text-[10px] text-slate-400 mb-2">Cap the total assets per type for the life of the agreement (subscription monthly caps configured separately).</p>
                  <div className="space-y-2">
                    {rights.generation_types.map(typeVal => {
                      const typeLabel = GENERATION_TYPES.find(g => g.value === typeVal)?.label || typeVal;
                      const av = rights.asset_volume[typeVal] || { unlimited: true, cap: '' };
                      return (
                        <div key={typeVal} className="flex items-center gap-3 text-xs" data-testid={`asset-volume-${typeVal}`}>
                          <span className="text-slate-700 w-44 shrink-0">{typeLabel}</span>
                          <label className="flex items-center gap-1.5 text-slate-600">
                            <input type="checkbox" checked={av.unlimited}
                              onChange={e => setRights(r => ({
                                ...r,
                                asset_volume: { ...r.asset_volume, [typeVal]: { unlimited: e.target.checked, cap: av.cap } },
                              }))}
                              className="rounded border-slate-300" />
                            Unlimited
                          </label>
                          {!av.unlimited && (
                            <Input type="number" value={av.cap} placeholder="Cap"
                              onChange={e => setRights(r => ({
                                ...r,
                                asset_volume: { ...r.asset_volume, [typeVal]: { unlimited: false, cap: e.target.value } },
                              }))}
                              className="h-7 text-xs border-slate-200 w-24" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Visual Representation */}
              <div>
                <Label className="text-xs text-slate-600 mb-1.5 block">Visual Representation</Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {VISUAL_REPRESENTATION_OPTIONS.map(o => (
                    <button key={o.value} type="button" data-testid={`visual-rep-${o.value}`}
                      onClick={() => setRights(r => ({ ...r, visual_representation: o.value }))}
                      className={`text-left p-2.5 rounded-lg border transition-all
                        ${rights.visual_representation === o.value ? 'bg-blue-50 border-blue-300' : 'bg-white border-slate-200 hover:border-blue-200'}`}>
                      <p className="text-xs font-semibold text-slate-800">{o.label}</p>
                      <p className="text-[10px] text-slate-500 mt-0.5">{o.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Permitted Alterations */}
              <div>
                <Label className="text-xs text-slate-600 mb-1.5 block">Permitted Alterations</Label>
                <p className="text-[10px] text-slate-400 mb-2">The following alterations to the creator's likeness are permitted (all default off).</p>
                <div className="space-y-1.5">
                  {PERMITTED_ALTERATIONS.map(o => {
                    const checked = rights.permitted_alterations.includes(o.value);
                    const handleChange = (target) => {
                      const enabling = target.checked;
                      if (enabling && o.deliberate) {
                        // Two-step ENABLE confirmation
                        setPendingDeliberateAlt(o);
                        return;
                      }
                      setRights(r => ({
                        ...r,
                        permitted_alterations: enabling
                          ? [...r.permitted_alterations, o.value]
                          : r.permitted_alterations.filter(x => x !== o.value),
                      }));
                    };
                    return (
                      <div key={o.value} className={`p-2 rounded-md border ${o.sensitive || o.deliberate ? 'bg-amber-50/40 border-amber-100' : 'border-transparent'}`}>
                        <label className="flex items-start gap-2 text-xs text-slate-700">
                          <input type="checkbox" checked={checked} data-testid={`alt-${o.value}`}
                            onChange={e => handleChange(e.target)}
                            className="rounded border-slate-300 mt-0.5" />
                          <div className="flex-1">
                            <span>{o.label}</span>
                            {o.sensitivityNote && (
                              <p className={`text-[10px] mt-0.5 ${o.deliberate ? 'text-amber-700 font-medium' : 'text-amber-600'}`}>
                                ⚠ {o.sensitivityNote}
                              </p>
                            )}
                          </div>
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Dialogue & Voice */}
              <div className="p-3 rounded-lg bg-white border border-slate-100">
                <label className="flex items-center gap-2 text-xs text-slate-700">
                  <input type="checkbox" data-testid="dialogue-permitted"
                    checked={rights.dialogue_permitted}
                    onChange={e => setRights(r => ({ ...r, dialogue_permitted: e.target.checked }))}
                    className="rounded border-slate-300" />
                  Generated content may include the creator's synthesized voice
                </label>
                {rights.dialogue_permitted && (
                  <div className="mt-3 ml-5 space-y-2 text-xs">
                    <label className="flex items-center gap-2 text-slate-600">
                      <input type="checkbox" data-testid="dialogue-scripted-only"
                        checked={rights.dialogue_scripted_only}
                        onChange={e => setRights(r => ({ ...r, dialogue_scripted_only: e.target.checked }))}
                        className="rounded border-slate-300" />
                      Scripted lines only — scripts must be agreed in campaign brief
                    </label>
                    <label className="flex items-center gap-2 text-slate-600">
                      <input type="checkbox" data-testid="statements-permitted"
                        checked={rights.statements_permitted}
                        onChange={e => setRights(r => ({ ...r, statements_permitted: e.target.checked }))}
                        className="rounded border-slate-300" />
                      Creator may appear to make statements or claims
                    </label>
                    {rights.statements_permitted && (
                      <div className="ml-5 mt-1.5 space-y-1.5">
                        <Label className="text-[10px] text-slate-500 block">Statements limited to</Label>
                        <Select value={rights.statement_scope} onValueChange={v => setRights(r => ({ ...r, statement_scope: v }))}>
                          <SelectTrigger data-testid="statement-scope" className="h-8 text-xs border-slate-200"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-white">
                            {STATEMENT_SCOPES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        {rights.statement_scope === 'other' && (
                          <Input data-testid="statement-scope-other" value={rights.statement_scope_other}
                            onChange={e => setRights(r => ({ ...r, statement_scope_other: e.target.value }))}
                            placeholder="Specify allowed statements" className="h-8 text-xs border-slate-200 mt-1" />
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Post-Generation Modifications */}
              <div>
                <Label className="text-xs text-slate-600 mb-1.5 block">Post-Generation Modifications</Label>
                <p className="text-[10px] text-slate-400 mb-2">After generation, the agency may apply the following modifications.</p>
                <div className="space-y-1.5">
                  {POST_GEN_MODIFICATIONS.map(o => (
                    <label key={o.value} className="flex items-center gap-2 text-xs text-slate-700">
                      <input type="checkbox" data-testid={`postgen-${o.value}`}
                        checked={rights.post_generation_modifications.includes(o.value)}
                        onChange={e => setRights(r => ({
                          ...r,
                          post_generation_modifications: e.target.checked
                            ? [...r.post_generation_modifications, o.value]
                            : r.post_generation_modifications.filter(x => x !== o.value),
                        }))}
                        className="rounded border-slate-300" />
                      {o.label}
                    </label>
                  ))}
                  <label className="flex items-center gap-2 text-xs text-slate-700 pt-1 border-t border-slate-50">
                    <input type="checkbox" data-testid="postgen-none"
                      checked={rights.post_generation_modifications.includes('none')}
                      onChange={e => setRights(r => ({
                        ...r,
                        post_generation_modifications: e.target.checked
                          ? ['none']
                          : r.post_generation_modifications.filter(x => x !== 'none'),
                      }))}
                      className="rounded border-slate-300" />
                    None — generated asset must be used as-is
                  </label>
                </div>
              </div>

              {/* AI Model Data Usage — Locked Platform Commitment */}
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-100" data-testid="ai-model-data-usage-lock">
                <div className="flex items-start gap-2">
                  <Lock className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs font-semibold text-slate-700">AI Model Data Usage — Platform Commitment</p>
                    <p className="text-[11px] text-slate-600 mt-0.5">
                      Generated assets and source likeness materials will <strong>not</strong> be used to train, fine-tune,
                      or improve any AI model beyond the scope of this campaign. This is a non-negotiable commitment.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </SectionToggle>

          {/* ── Section 4.3: Permitted Use & Restrictions ── */}
          <SectionToggle id="permitted_use" title="Permitted Use & Restrictions" icon={Eye} open={openSections.permitted_use} onToggle={() => toggle('permitted_use')}
            complete={permittedUseComplete} required>
            <div className="space-y-3 mt-2">
              <ChipSelect testid="purpose-of-use" label="Purpose of Use *" options={PURPOSE_OF_USE} selected={rights.purpose_of_use}
                onChange={v => setRights(r => ({ ...r, purpose_of_use: v }))} />

              <div>
                <Label className="text-xs text-slate-600 mb-1 block">Usage Scope *</Label>
                <Select value={rights.usage_scope} onValueChange={v => setRights(r => ({ ...r, usage_scope: v }))}>
                  <SelectTrigger data-testid="usage-scope" className="h-8 text-xs border-slate-200"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="single_campaign">Single Campaign — assets used only for this campaign</SelectItem>
                    <SelectItem value="reusable">Reusable — assets may be reused under new agreement</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                <p className="text-xs font-semibold text-slate-700 mb-2">Content Restrictions *</p>
                <p className="text-[10px] text-slate-500 mb-2">Agency confirms content will not include the following.</p>
                <div className="space-y-1.5">
                  {CONTENT_RESTRICTIONS_LOCKED.map(o => (
                    <label key={o.value} className="flex items-center gap-2 text-xs text-slate-700 opacity-90">
                      <input type="checkbox" checked disabled className="rounded border-slate-300" />
                      <span>{o.label}</span>
                      <Lock className="w-3 h-3 text-slate-400" />
                    </label>
                  ))}
                  {CONTENT_RESTRICTIONS_OPTIONAL.map(o => (
                    <label key={o.value} className="flex items-center gap-2 text-xs text-slate-700">
                      <input type="checkbox" checked={rights.content_restrictions.includes(o.value)}
                        onChange={e => {
                          if (!e.target.checked) {
                            if (!window.confirm(`Removing "${o.label}" will be visible to the creator. Are you sure?`)) return;
                          }
                          setRights(r => ({
                            ...r,
                            content_restrictions: e.target.checked
                              ? [...r.content_restrictions, o.value]
                              : r.content_restrictions.filter(x => x !== o.value),
                          }));
                        }}
                        className="rounded border-slate-300" />
                      {o.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="p-3 rounded-lg bg-white border border-slate-100">
                <label className="flex items-center gap-2 text-xs text-slate-700 mb-2">
                  <input type="checkbox" checked={rights.competitor_restriction}
                    onChange={e => setRights(r => ({ ...r, competitor_restriction: e.target.checked }))}
                    className="rounded border-slate-300" />
                  Competitor restriction
                </label>
                {rights.competitor_restriction && (
                  <Input data-testid="competitor-names" value={rights.competitor_names}
                    onChange={e => setRights(r => ({ ...r, competitor_names: e.target.value }))}
                    placeholder="Name competitor brands or categories"
                    className="h-8 text-xs border-slate-200 mt-1" />
                )}
              </div>

              <div className="grid grid-cols-1 gap-3">
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">Output Reuse After Term *</Label>
                  <Select value={rights.output_reuse} onValueChange={v => setRights(r => ({ ...r, output_reuse: v }))}>
                    <SelectTrigger data-testid="output-reuse" className="h-8 text-xs border-slate-200"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {OUTPUT_REUSE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">Source Material Reuse *</Label>
                  <Select value={rights.source_material_reuse} onValueChange={v => setRights(r => ({ ...r, source_material_reuse: v }))}>
                    <SelectTrigger data-testid="source-reuse" className="h-8 text-xs border-slate-200"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {SOURCE_REUSE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <label className="flex items-center gap-2 text-xs text-amber-700 p-2 rounded-md bg-amber-50/50 border border-amber-100">
                <input type="checkbox" data-testid="endorsement-toggle" checked={rights.endorsement_requested}
                  onChange={e => setRights(r => ({ ...r, endorsement_requested: e.target.checked }))}
                  className="rounded border-amber-300" />
                <span className="flex-1">Endorsement requested — creator's likeness implies personal endorsement</span>
              </label>
              {rights.endorsement_requested && (
                <div className="p-2 bg-amber-50 border border-amber-200 rounded-md text-[10px] text-amber-800">
                  ⚠ FTC / ASA disclosure obligations apply when endorsement is implied. Ensure published content includes required disclosures.
                </div>
              )}
            </div>
          </SectionToggle>

          {/* ── Section 4.4: Asset Ownership ── */}
          <SectionToggle id="ownership" title="Asset Ownership" icon={Star} open={openSections.ownership} onToggle={() => toggle('ownership')}
            complete={ownershipComplete} required>
            <div className="space-y-3 mt-2">
              <div>
                <Label className="text-xs text-slate-600 mb-1.5 block">Ownership of Generated Assets *</Label>
                <div className="space-y-2">
                  {ASSET_OWNERSHIP_OPTIONS.map(o => (
                    <button key={o.value} type="button" data-testid={`ownership-${o.value}`}
                      onClick={() => setRights(r => ({
                        ...r, asset_ownership: o.value,
                        creator_portfolio_rights: o.value === 'agency_owns_creator_portfolio' ? true : r.creator_portfolio_rights,
                      }))}
                      className={`w-full text-left p-2.5 rounded-lg border transition-all
                        ${rights.asset_ownership === o.value ? 'bg-blue-50 border-blue-300' : 'bg-white border-slate-200 hover:border-blue-200'}`}>
                      <p className="text-xs font-semibold text-slate-800">{o.label}</p>
                      <p className="text-[10px] text-slate-500 mt-0.5">{o.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              <label className="flex items-center gap-2 text-xs text-slate-700">
                <input type="checkbox" data-testid="creator-portfolio-rights"
                  checked={rights.creator_portfolio_rights}
                  onChange={e => setRights(r => ({ ...r, creator_portfolio_rights: e.target.checked }))}
                  className="rounded border-slate-300" />
                Creator may display generated assets in their own portfolio and promotional materials
              </label>

              <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                <p className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1">
                  <Lock className="w-3 h-3 text-slate-400" /> Underlying IP — Non-Negotiable Commitments
                </p>
                <ul className="text-[11px] text-slate-600 space-y-1 ml-1">
                  <li>• Creator retains full ownership of their likeness, name, voice, and persona at all times.</li>
                  <li>• This license does not transfer any underlying intellectual property.</li>
                  <li>• Generated assets will not be used to train or improve any AI model beyond this campaign.</li>
                  <li>• Agency's rights are limited to the scope defined in this agreement.</li>
                </ul>
              </div>

              <div>
                <Label className="text-xs text-slate-600 mb-1 block">Moral Rights *</Label>
                <Select value={rights.moral_rights} onValueChange={v => setRights(r => ({ ...r, moral_rights: v }))}>
                  <SelectTrigger data-testid="moral-rights" className="h-8 text-xs border-slate-200"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {MORAL_RIGHTS_OPTIONS.map(o => (
                      <SelectItem key={o.value} value={o.value}
                        disabled={moralRightsStrong && o.value === 'waived_for_campaign'}>
                        {o.label}{moralRightsStrong && o.value === 'waived_for_campaign' ? ' (unavailable in selected jurisdiction)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-slate-400 mt-1">
                  Moral rights enforceability varies by jurisdiction. Governing law: <span className="font-medium">{govLaw || 'not set'}</span>.
                </p>
              </div>
            </div>
          </SectionToggle>

          {/* ── Section 4.5: Subscription License Terms (subscription/hybrid only) ── */}
          {subscriptionActive && (
            <SectionToggle id="subscription" title="Subscription License Terms" icon={Calendar} open={openSections.subscription} onToggle={() => toggle('subscription')}
              complete={subscriptionComplete} required
              badge={<Badge variant="outline" className="text-[9px] ml-1 capitalize">{rights.subscription_billing_frequency}</Badge>}>
              <div className="space-y-4 mt-2">
                <div className="p-2.5 bg-emerald-50/50 rounded-md border border-emerald-100">
                  <p className="text-[11px] text-emerald-800">
                    Active because Deal Structure is <strong className="capitalize">{offer.deal_structure}</strong>.
                    Recurring fee is set in <strong>Commercial Offer</strong>: {offer.currency} {Number(offer.recurring_amount || 0).toLocaleString()} per period.
                  </p>
                </div>

                {/* Billing Frequency */}
                <div>
                  <Label className="text-xs text-slate-600 mb-1.5 block">Billing Frequency *</Label>
                  <div className="grid grid-cols-4 gap-2">
                    {BILLING_FREQUENCIES.map(o => (
                      <button key={o.value} type="button" data-testid={`billing-${o.value}`}
                        onClick={() => setRights(r => ({ ...r, subscription_billing_frequency: o.value }))}
                        className={`py-2 rounded-lg border text-xs font-medium transition-all
                          ${rights.subscription_billing_frequency === o.value ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'}`}>
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Generation Caps */}
                <div className="p-3 rounded-lg bg-white border border-slate-100">
                  <label className="flex items-center gap-2 text-xs text-slate-700 mb-2">
                    <input type="checkbox" data-testid="gen-caps-enabled"
                      checked={rights.generation_caps_enabled}
                      onChange={e => setRights(r => ({ ...r, generation_caps_enabled: e.target.checked }))}
                      className="rounded border-slate-300" />
                    Apply per-period generation caps
                  </label>
                  {rights.generation_caps_enabled && (
                    <div className="ml-1 space-y-2">
                      <p className="text-[10px] text-slate-500">Max new assets per billing period for each generation type. (Lifetime caps from Section 4.2 still apply — whichever hits first governs.)</p>
                      {rights.generation_types.length === 0 ? (
                        <p className="text-[11px] text-amber-600">⚠ Pick at least one generation type in Section 4.2 first.</p>
                      ) : (
                        <div className="space-y-1.5">
                          {rights.generation_types.map(typeVal => {
                            const typeLabel = GENERATION_TYPES.find(g => g.value === typeVal)?.label || typeVal;
                            const gc = rights.generation_caps[typeVal] || { unlimited: true, cap: '' };
                            return (
                              <div key={typeVal} className="flex items-center gap-3 text-xs" data-testid={`gen-cap-${typeVal}`}>
                                <span className="text-slate-700 w-44 shrink-0">{typeLabel}</span>
                                <label className="flex items-center gap-1.5 text-slate-600">
                                  <input type="checkbox" checked={gc.unlimited}
                                    onChange={e => setRights(r => ({
                                      ...r,
                                      generation_caps: { ...r.generation_caps, [typeVal]: { unlimited: e.target.checked, cap: gc.cap } },
                                    }))}
                                    className="rounded border-slate-300" />
                                  Unlimited
                                </label>
                                {!gc.unlimited && (
                                  <Input type="number" value={gc.cap} placeholder="Cap"
                                    onChange={e => setRights(r => ({
                                      ...r,
                                      generation_caps: { ...r.generation_caps, [typeVal]: { unlimited: false, cap: e.target.value } },
                                    }))}
                                    className="h-7 text-xs border-slate-200 w-24" />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Unused allowance */}
                      <div className="pt-2 border-t border-slate-100 mt-2">
                        <Label className="text-[10px] text-slate-500 mb-1 block">Unused Allowance Behavior</Label>
                        <div className="grid grid-cols-2 gap-2">
                          {UNUSED_ALLOWANCE.map(o => (
                            <button key={o.value} type="button" data-testid={`unused-${o.value}`}
                              onClick={() => setRights(r => ({ ...r, unused_allowance_behavior: o.value }))}
                              className={`text-left p-2 rounded-md border transition-all
                                ${rights.unused_allowance_behavior === o.value ? 'bg-blue-50 border-blue-300' : 'bg-white border-slate-200 hover:border-blue-200'}`}>
                              <p className="text-[11px] font-semibold text-slate-700">{o.label}</p>
                              <p className="text-[9px] text-slate-500">{o.desc}</p>
                            </button>
                          ))}
                        </div>
                        {rights.unused_allowance_behavior === 'rolls_over' && (
                          <div className="mt-2">
                            <Label className="text-[10px] text-slate-500 mb-1 block">Rollover Cap (blank = unlimited)</Label>
                            <Input type="number" value={rights.rollover_cap}
                              onChange={e => setRights(r => ({ ...r, rollover_cap: e.target.value }))}
                              placeholder="e.g. 10" className="h-7 text-xs border-slate-200 w-32" />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Content Reuse Scope */}
                <div>
                  <Label className="text-xs text-slate-600 mb-1.5 block">Content Reuse vs New Generation *</Label>
                  <p className="text-[10px] text-slate-400 mb-2">Within each billing period, the subscription covers:</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {CONTENT_REUSE_SCOPE.map(o => (
                      <button key={o.value} type="button" data-testid={`reuse-scope-${o.value}`}
                        onClick={() => setRights(r => ({ ...r, content_reuse_scope: o.value }))}
                        className={`text-left p-2.5 rounded-lg border transition-all
                          ${rights.content_reuse_scope === o.value ? 'bg-blue-50 border-blue-300' : 'bg-white border-slate-200 hover:border-blue-200'}`}>
                        <p className="text-xs font-semibold text-slate-800">{o.label}</p>
                        <p className="text-[10px] text-slate-500 mt-0.5">{o.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Subscription Exclusivity */}
                <div>
                  <Label className="text-xs text-slate-600 mb-1.5 block">Exclusivity During Subscription</Label>
                  <p className="text-[10px] text-slate-400 mb-2">Applies during the active subscription period. Can differ from overall License Scope exclusivity (Section 4.1).</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    {SUBSCRIPTION_EXCLUSIVITY.map(o => (
                      <button key={o.value} type="button" data-testid={`sub-excl-${o.value}`}
                        onClick={() => setRights(r => ({ ...r, subscription_exclusivity: o.value }))}
                        className={`text-left p-2.5 rounded-lg border transition-all
                          ${rights.subscription_exclusivity === o.value ? 'bg-blue-50 border-blue-300' : 'bg-white border-slate-200 hover:border-blue-200'}`}>
                        <p className="text-xs font-semibold text-slate-800">{o.label}</p>
                        <p className="text-[10px] text-slate-500 mt-0.5">{o.desc}</p>
                      </button>
                    ))}
                  </div>
                  {rights.subscription_exclusivity === 'category_exclusive' && (
                    <div className="mt-2">
                      <ChipSelect testid="sub-excl-categories" label="Categories *" options={EXCLUSIVITY_CATEGORIES}
                        selected={rights.subscription_exclusivity_categories}
                        onChange={v => setRights(r => ({ ...r, subscription_exclusivity_categories: v }))} />
                    </div>
                  )}
                </div>

                {/* Suspension */}
                <div className="p-3 rounded-lg bg-white border border-slate-100">
                  <label className="flex items-center gap-2 text-xs text-slate-700">
                    <input type="checkbox" data-testid="suspension-permitted"
                      checked={rights.suspension_permitted}
                      onChange={e => setRights(r => ({ ...r, suspension_permitted: e.target.checked }))}
                      className="rounded border-slate-300" />
                    Agency may temporarily suspend the subscription without terminating
                  </label>
                  {rights.suspension_permitted && (
                    <div className="mt-2 ml-5 space-y-2">
                      <div className="grid grid-cols-2 gap-3 items-end">
                        <div>
                          <Label className="text-[10px] text-slate-500 mb-1 block">Max suspension period (days)</Label>
                          <Input type="number" value={rights.suspension_max_days}
                            onChange={e => setRights(r => ({ ...r, suspension_max_days: e.target.value }))}
                            data-testid="suspension-max-days" className="h-8 text-xs border-slate-200" />
                        </div>
                        <div>
                          <Label className="text-[10px] text-slate-500 mb-1 block">Per</Label>
                          <Select value={rights.suspension_period_basis} onValueChange={v => setRights(r => ({ ...r, suspension_period_basis: v }))}>
                            <SelectTrigger className="h-8 text-xs border-slate-200"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-white">
                              <SelectItem value="billing_period">Billing Period</SelectItem>
                              <SelectItem value="year">Year</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <label className="flex items-center gap-2 text-xs text-slate-600">
                        <input type="checkbox" data-testid="suspension-fee-continues"
                          checked={rights.suspension_fee_continues}
                          onChange={e => setRights(r => ({ ...r, suspension_fee_continues: e.target.checked }))}
                          className="rounded border-slate-300" />
                        Creator continues to receive fee during suspension
                      </label>
                      <p className="text-[10px] text-slate-400">New generation is paused during suspension (locked).</p>
                    </div>
                  )}
                </div>

                {/* Price change */}
                <div>
                  <Label className="text-xs text-slate-600 mb-1.5 block">Price Change on Renewal</Label>
                  <div className="space-y-2">
                    {PRICE_CHANGE_POLICY.map(o => (
                      <button key={o.value} type="button" data-testid={`price-change-${o.value}`}
                        onClick={() => setRights(r => ({ ...r, price_change_policy: o.value }))}
                        className={`w-full text-left p-2.5 rounded-lg border transition-all
                          ${rights.price_change_policy === o.value ? 'bg-blue-50 border-blue-300' : 'bg-white border-slate-200 hover:border-blue-200'}`}>
                        <p className="text-xs font-semibold text-slate-800">{o.label}</p>
                        <p className="text-[10px] text-slate-500 mt-0.5">{o.desc}</p>
                      </button>
                    ))}
                  </div>
                  {rights.price_change_policy === 'agency_with_notice' && (
                    <div className="mt-2 ml-1">
                      <Label className="text-[10px] text-slate-500 mb-1 block">Notice required (days) *</Label>
                      <Input type="number" value={rights.price_change_notice_days}
                        onChange={e => setRights(r => ({ ...r, price_change_notice_days: e.target.value }))}
                        data-testid="price-change-notice" className="h-8 text-xs border-slate-200 w-32" />
                    </div>
                  )}
                </div>

                {/* Cancellation */}
                <div className="p-3 rounded-lg bg-slate-50 border border-slate-100 space-y-3">
                  <p className="text-xs font-semibold text-slate-700">Cancellation Terms *</p>

                  <div className="space-y-2">
                    <p className="text-[11px] text-slate-600 font-medium">Agency cancellation</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-[10px] text-slate-500 mb-1 block">Notice required (days) *</Label>
                        <Input type="number" value={rights.agency_cancellation_notice_days}
                          onChange={e => setRights(r => ({ ...r, agency_cancellation_notice_days: e.target.value }))}
                          data-testid="agency-cancel-notice" className="h-8 text-xs border-slate-200" />
                      </div>
                      <div>
                        <Label className="text-[10px] text-slate-500 mb-1 block">Fee owed *</Label>
                        <Select value={rights.agency_cancellation_fee} onValueChange={v => setRights(r => ({ ...r, agency_cancellation_fee: v }))}>
                          <SelectTrigger data-testid="agency-cancel-fee" className="h-8 text-xs border-slate-200"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-white">
                            {CANCELLATION_FEE.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 pt-2 border-t border-slate-200">
                    <p className="text-[11px] text-slate-600 font-medium">Creator withdrawal of consent</p>
                    <div>
                      <Label className="text-[10px] text-slate-500 mb-1 block">Notice required (days) *</Label>
                      <Input type="number" value={rights.creator_withdrawal_notice_days}
                        onChange={e => setRights(r => ({ ...r, creator_withdrawal_notice_days: e.target.value }))}
                        data-testid="creator-withdrawal-notice" className="h-8 text-xs border-slate-200 w-32" />
                    </div>
                    <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-100 rounded p-2">
                      ⚠ Creator withdrawal mid-subscription is a specific legal consideration. Ensure termination terms in Section 4.6 (coming) address this clearly.
                    </p>
                  </div>
                </div>

                {/* Accumulated Assets Post-Subscription */}
                <div>
                  <Label className="text-xs text-slate-600 mb-1.5 block">Accumulated Assets After Subscription *</Label>
                  <p className="text-[10px] text-slate-400 mb-2">What happens to content generated during the subscription after it ends?</p>
                  <div className="space-y-2">
                    {ACCUMULATED_ASSETS_POLICY.map(o => (
                      <button key={o.value} type="button" data-testid={`accum-${o.value}`}
                        onClick={() => setRights(r => ({ ...r, accumulated_assets_post_subscription: o.value }))}
                        className={`w-full text-left p-2.5 rounded-lg border transition-all
                          ${rights.accumulated_assets_post_subscription === o.value ? 'bg-blue-50 border-blue-300' : 'bg-white border-slate-200 hover:border-blue-200'}`}>
                        <p className="text-xs font-semibold text-slate-800">{o.label}</p>
                        <p className="text-[10px] text-slate-500 mt-0.5">{o.desc}</p>
                      </button>
                    ))}
                  </div>
                  {(rights.accumulated_assets_post_subscription === 'must_takedown' || rights.accumulated_assets_post_subscription === 'remain_live_period') && (
                    <div className="mt-2">
                      <Label className="text-[10px] text-slate-500 mb-1 block">
                        {rights.accumulated_assets_post_subscription === 'must_takedown' ? 'Takedown window (days) *' : 'Remain-live duration (days) *'}
                      </Label>
                      <Input type="number" value={rights.accumulated_assets_days}
                        onChange={e => setRights(r => ({ ...r, accumulated_assets_days: e.target.value }))}
                        data-testid="accum-days" className="h-8 text-xs border-slate-200 w-32" />
                    </div>
                  )}
                </div>
              </div>
            </SectionToggle>
          )}

          {/* ── Section 4.6: Termination & Expiry ── */}
          <SectionToggle id="termination" title="Termination & Expiry" icon={Shield} open={openSections.termination} onToggle={() => toggle('termination')}
            complete={terminationComplete} required>
            <div className="space-y-4 mt-2">

              {/* Termination triggers */}
              <div>
                <Label className="text-xs text-slate-600 mb-1.5 block">Termination for Cause — Agency may terminate if *</Label>
                <p className="text-[10px] text-slate-400 mb-2">Select all conditions under which the agency may terminate the agreement.</p>
                <div className="space-y-1.5">
                  {TERMINATION_TRIGGERS.map(o => (
                    <label key={o.value} className="flex items-start gap-2 text-xs text-slate-700 p-2 rounded-md border border-slate-100 hover:bg-slate-50">
                      <input type="checkbox" data-testid={`trigger-${o.value}`}
                        checked={rights.termination_triggers.includes(o.value)}
                        onChange={e => setRights(r => ({
                          ...r,
                          termination_triggers: e.target.checked
                            ? [...r.termination_triggers, o.value]
                            : r.termination_triggers.filter(x => x !== o.value),
                        }))}
                        className="rounded border-slate-300 mt-0.5" />
                      <span>{o.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Cure period */}
              <div>
                <Label className="text-xs text-slate-600 mb-1.5 block">Cure period before termination is effective *</Label>
                <Select value={rights.cure_period} onValueChange={v => setRights(r => ({ ...r, cure_period: v }))}>
                  <SelectTrigger data-testid="cure-period" className="h-8 text-xs border-slate-200 max-w-xs"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {CURE_PERIODS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Morality Clause */}
              <div className="p-3 rounded-lg bg-white border border-slate-100 space-y-2">
                <label className="flex items-center gap-2 text-xs text-slate-700">
                  <input type="checkbox" data-testid="morality-clause"
                    checked={rights.morality_clause}
                    onChange={e => setRights(r => ({ ...r, morality_clause: e.target.checked }))}
                    className="rounded border-slate-300" />
                  Morality clause — agency may terminate for conduct materially damaging to the brand
                </label>
                {rights.morality_clause && (
                  <div className="ml-5 space-y-2">
                    <Label className="text-[10px] text-slate-500 block">Conditions (select at least one)</Label>
                    <div className="space-y-1">
                      {MORALITY_CONDITIONS.map(o => (
                        <label key={o.value} className="flex items-center gap-2 text-xs text-slate-700">
                          <input type="checkbox" data-testid={`morality-${o.value}`}
                            checked={rights.morality_conditions.includes(o.value)}
                            onChange={e => setRights(r => ({
                              ...r,
                              morality_conditions: e.target.checked
                                ? [...r.morality_conditions, o.value]
                                : r.morality_conditions.filter(x => x !== o.value),
                            }))}
                            className="rounded border-slate-300" />
                          {o.label}
                        </label>
                      ))}
                    </div>
                    <Input data-testid="morality-other" value={rights.morality_other}
                      onChange={e => setRights(r => ({ ...r, morality_other: e.target.value }))}
                      placeholder="Other conditions (optional)" className="h-8 text-xs border-slate-200" />
                    <div className="pt-2 border-t border-slate-100">
                      <Label className="text-[10px] text-slate-500 mb-1 block">Consequence of trigger</Label>
                      <Select value={rights.morality_consequence} onValueChange={v => setRights(r => ({ ...r, morality_consequence: v }))}>
                        <SelectTrigger data-testid="morality-consequence" className="h-8 text-xs border-slate-200"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white">
                          {MORALITY_CONSEQUENCES.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
              </div>

              {/* Early Termination by Agency Without Cause */}
              <div className="p-3 rounded-lg bg-white border border-slate-100 space-y-2">
                <label className="flex items-center gap-2 text-xs text-slate-700">
                  <input type="checkbox" data-testid="early-termination-permitted"
                    checked={rights.early_termination_permitted}
                    onChange={e => setRights(r => ({ ...r, early_termination_permitted: e.target.checked }))}
                    className="rounded border-slate-300" />
                  Early termination by agency without cause permitted
                </label>
                {rights.early_termination_permitted && (
                  <div className="ml-5 space-y-2">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-[10px] text-slate-500 mb-1 block">Notice period (days) *</Label>
                        <Input type="number" value={rights.early_termination_notice_days}
                          onChange={e => setRights(r => ({ ...r, early_termination_notice_days: e.target.value }))}
                          data-testid="early-term-notice" className="h-8 text-xs border-slate-200" />
                      </div>
                      <div>
                        <Label className="text-[10px] text-slate-500 mb-1 block">Compensation *</Label>
                        <Select value={rights.early_termination_compensation} onValueChange={v => setRights(r => ({ ...r, early_termination_compensation: v }))}>
                          <SelectTrigger data-testid="early-term-comp" className="h-8 text-xs border-slate-200"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-white">
                            {EARLY_TERMINATION_COMPENSATION.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {rights.early_termination_compensation === 'kill_fee' && (
                      <div>
                        <Label className="text-[10px] text-slate-500 mb-1 block">Kill fee % of total agreed fee *</Label>
                        <Input type="number" min={0} max={100} value={rights.early_termination_kill_fee_percentage}
                          onChange={e => setRights(r => ({ ...r, early_termination_kill_fee_percentage: e.target.value }))}
                          data-testid="early-term-killfee" className="h-8 text-xs border-slate-200 w-32" />
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Post-Termination Obligations */}
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-100 space-y-3">
                <p className="text-xs font-semibold text-slate-700">Post-Termination Obligations *</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] text-slate-500 mb-1 block">Takedown all published assets within (days) *</Label>
                    <Input type="number" value={rights.post_termination_takedown_days}
                      onChange={e => setRights(r => ({ ...r, post_termination_takedown_days: e.target.value }))}
                      data-testid="post-term-takedown-days" className="h-8 text-xs border-slate-200" />
                  </div>
                  <div>
                    <Label className="text-[10px] text-slate-500 mb-1 block">Delete all generated assets within (days) *</Label>
                    <Input type="number" value={rights.post_termination_deletion_days}
                      onChange={e => setRights(r => ({ ...r, post_termination_deletion_days: e.target.value }))}
                      data-testid="post-term-deletion-days" className="h-8 text-xs border-slate-200" />
                  </div>
                </div>

                <div className="pt-2 border-t border-slate-200 space-y-2">
                  <label className="flex items-center gap-2 text-xs text-slate-700">
                    <input type="checkbox" data-testid="residual-use-permitted"
                      checked={rights.residual_use_permitted}
                      onChange={e => setRights(r => ({ ...r, residual_use_permitted: e.target.checked }))}
                      className="rounded border-slate-300" />
                    Residual use period for assets in physical / print production
                  </label>
                  {rights.residual_use_permitted && (
                    <div className="ml-5 space-y-2">
                      <div>
                        <Label className="text-[10px] text-slate-500 mb-1 block">Residual period (days) *</Label>
                        <Input type="number" value={rights.residual_use_days}
                          onChange={e => setRights(r => ({ ...r, residual_use_days: e.target.value }))}
                          data-testid="residual-days" className="h-8 text-xs border-slate-200 w-32" />
                      </div>
                      <ChipSelect testid="residual-channels" label="Limited to channels *" options={RESIDUAL_USE_CHANNELS}
                        selected={rights.residual_use_channels}
                        onChange={v => setRights(r => ({ ...r, residual_use_channels: v }))} />
                    </div>
                  )}
                </div>

                <label className="flex items-center gap-2 text-xs text-slate-700 pt-2 border-t border-slate-200">
                  <input type="checkbox" data-testid="deletion-confirmation"
                    checked={rights.deletion_confirmation_required}
                    onChange={e => setRights(r => ({ ...r, deletion_confirmation_required: e.target.checked }))}
                    className="rounded border-slate-300" />
                  Written confirmation of asset deletion provided to creator
                </label>
              </div>

              {/* Governing Law (per-proposal override) */}
              <div className="p-3 rounded-lg bg-white border border-slate-100 space-y-2">
                <Label className="text-xs text-slate-600 block">Governing Law *</Label>
                <p className="text-[10px] text-slate-400">
                  Default from agency settings: <span className="font-medium">{agencyGovLaw || 'not set'}</span>. Override per-proposal if needed.
                </p>
                <Select value={rights.governing_law || agencyGovLaw || ''}
                  onValueChange={v => setRights(r => ({ ...r, governing_law: v }))}>
                  <SelectTrigger data-testid="proposal-governing-law" className="h-8 text-xs border-slate-200 max-w-xs"><SelectValue placeholder="Select jurisdiction" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {GOVERNING_LAW_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Dispute Resolution */}
              <div>
                <Label className="text-xs text-slate-600 mb-1.5 block">Dispute Resolution *</Label>
                {agencyDispute && !rights.dispute_resolution && (
                  <p className="text-[10px] text-slate-400 mb-2">
                    Default from agency settings: <span className="font-medium capitalize">{(DISPUTE_RESOLUTION.find(o => o.value === agencyDispute)?.label) || agencyDispute.replace(/_/g, ' ')}</span>. Override below if needed.
                  </p>
                )}
                <div className="space-y-2">
                  {DISPUTE_RESOLUTION.map(o => {
                    const effective = rights.dispute_resolution || agencyDispute;
                    return (
                      <button key={o.value} type="button" data-testid={`dispute-${o.value}`}
                        onClick={() => setRights(r => ({ ...r, dispute_resolution: o.value }))}
                        className={`w-full text-left p-2.5 rounded-lg border transition-all
                          ${effective === o.value ? 'bg-blue-50 border-blue-300' : 'bg-white border-slate-200 hover:border-blue-200'}`}>
                        <p className="text-xs font-semibold text-slate-800">{o.label}</p>
                        <p className="text-[10px] text-slate-500 mt-0.5">{o.desc}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

            </div>
          </SectionToggle>

          {/* ── Section 5: Agreement Extensions ── */}
          <SectionToggle id="extensions" title="Agreement Extensions" icon={Calendar} open={openSections.extensions} onToggle={() => toggle('extensions')}
            complete>
            <div className="space-y-3 mt-2">
              <div>
                <Label className="text-xs text-slate-600 mb-1 block">Pricing Method (campaign-level)</Label>
                <Select value={pricingMethod} onValueChange={setPricingMethod}>
                  <SelectTrigger data-testid="pricing-method-select" className="h-8 text-xs border-slate-200"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="fixed_per_submission">Fixed Per Submission</SelectItem>
                    <SelectItem value="open_pricing">Open Pricing — creator confirms per submission</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <label className="flex items-center gap-2 text-xs text-slate-700">
                <input type="checkbox" checked={continuationAllowed}
                  onChange={e => setContinuationAllowed(e.target.checked)}
                  data-testid="continuation-allowed" className="rounded border-slate-300" />
                Allow campaign continuation / renewal under same agreement
              </label>

              <label className="flex items-center gap-2 text-xs text-amber-700">
                <input type="checkbox" checked={royaltyEnabled}
                  onChange={e => setRoyaltyEnabled(e.target.checked)}
                  data-testid="royalty-enabled" className="rounded border-amber-300" />
                Royalty agreement
              </label>
              {royaltyEnabled && (
                <div className="p-3 bg-amber-50/30 rounded-lg border border-amber-100 space-y-2">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[10px] text-slate-500 mb-1 block">Royalty Type</Label>
                      <Select value={royaltyTerms.royalty_type} onValueChange={v => setRoyaltyTerms(p => ({ ...p, royalty_type: v }))}>
                        <SelectTrigger className="h-8 text-xs border-slate-200"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white">
                          <SelectItem value="revenue_share">Revenue Share</SelectItem>
                          <SelectItem value="ad_spend_percentage">Ad Spend Percentage</SelectItem>
                          <SelectItem value="fixed_percentage">Fixed Percentage</SelectItem>
                          <SelectItem value="usage_based">Usage-Based</SelectItem>
                          <SelectItem value="custom">Custom</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[10px] text-slate-500 mb-1 block">Royalty Rate (%)</Label>
                      <Input type="number" step="0.1" value={royaltyTerms.royalty_rate}
                        onChange={e => setRoyaltyTerms(p => ({ ...p, royalty_rate: e.target.value }))}
                        className="h-8 text-xs border-slate-200" />
                    </div>
                  </div>
                  <Input value={royaltyTerms.calculation_basis}
                    onChange={e => setRoyaltyTerms(p => ({ ...p, calculation_basis: e.target.value }))}
                    placeholder="Calculation basis (e.g. Net revenue from campaign ads)"
                    className="h-8 text-xs border-slate-200" />
                </div>
              )}
            </div>
          </SectionToggle>

          {/* External Contract URL (optional escape hatch) */}
          <div className="bg-white rounded-xl border border-slate-100 p-3">
            <Label className="text-xs text-slate-600 mb-1 block">External Contract URL (optional)</Label>
            <Input value={rights.contract_url}
              onChange={e => setRights(r => ({ ...r, contract_url: e.target.value }))}
              placeholder="https://..." className="h-8 text-xs border-slate-200" />
            <p className="text-[10px] text-slate-400 mt-1">For agencies using their own paper contracts in addition to platform terms.</p>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <Button data-testid="save-draft-btn" variant="outline" onClick={() => saveProposal('draft')} disabled={saving}
              className="text-xs h-9 border-slate-200">
              <Bookmark className="w-3 h-3 mr-1" />Save Draft
            </Button>
            <Button data-testid="send-proposal-btn"
              onClick={() => saveProposal(deal.proposal_status === 'rejected' || deal.proposal_status === 'countered' ? 'resubmit' : 'send')}
              disabled={saving || !allRequiredComplete || !agencyGovLaw}
              className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-9 disabled:opacity-50">
              <Send className="w-3 h-3 mr-1" />
              {saving ? 'Saving...' : deal.proposal_status === 'rejected' || deal.proposal_status === 'countered' ? 'Resubmit Proposal' : 'Send Proposal'}
            </Button>
            {!isDraft && (
              <Button variant="ghost" onClick={() => setEditing(false)} className="text-xs h-9 text-slate-400">Cancel</Button>
            )}
          </div>
        </div>
      )}

      {/* Preset Dialog */}
      <Dialog open={presetMenuOpen} onOpenChange={setPresetMenuOpen}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Apply a Preset</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">Pre-fill standard fields. You can edit anything afterwards.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {presets.map(p => (
              <button key={p.id} type="button" data-testid={`preset-${p.id}`}
                onClick={() => applyPreset(p)}
                className="w-full text-left p-3 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50/30 transition-colors flex items-start gap-3">
                <Zap className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-slate-800">{p.name}</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">{p.description}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-300 mt-0.5 shrink-0" />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Deliberate Alteration ENABLE Confirm */}
      <SensitiveToggleConfirm
        open={!!pendingDeliberateAlt}
        onOpenChange={(o) => { if (!o) setPendingDeliberateAlt(null); }}
        label={pendingDeliberateAlt?.label || ''}
        onConfirm={() => {
          if (!pendingDeliberateAlt) return;
          setRights(r => ({
            ...r,
            permitted_alterations: [...r.permitted_alterations, pendingDeliberateAlt.value],
          }));
          setPendingDeliberateAlt(null);
        }}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// REFERENCES EDITOR
// ═══════════════════════════════════════════════════════════

function ReferencesEditor({ refs, onChange }) {
  const [val, setVal] = useState('');
  const add = () => { if (val.trim()) { onChange([...(refs || []), val.trim()]); setVal(''); } };
  return (
    <div>
      <Label className="text-xs text-slate-600 mb-1 block">References (optional)</Label>
      <div className="flex gap-2">
        <Input value={val} onChange={e => setVal(e.target.value)} placeholder="Add a mood-board / reference URL"
          className="h-8 text-xs border-slate-200 flex-1" />
        <Button size="sm" type="button" variant="outline" onClick={add} className="h-8 text-xs">Add</Button>
      </div>
      {refs?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {refs.map((r, i) => (
            <span key={i} className="px-2 py-0.5 bg-slate-50 border border-slate-200 rounded-md text-[10px] text-slate-600 flex items-center gap-1">
              {r.length > 30 ? r.slice(0, 30) + '…' : r}
              <button type="button" onClick={() => onChange(refs.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-500">×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// VIEW FULL TERMS EXPANDER
// ═══════════════════════════════════════════════════════════

function ViewFullTermsExpander({ rights, offer }) {
  const [open, setOpen] = useState(false);
  if (!rights || Object.keys(rights).length === 0) return null;
  return (
    <div className="mt-4 pt-3 border-t border-slate-100">
      <button onClick={() => setOpen(!open)} type="button" data-testid="view-full-terms"
        className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors">
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        View Full Terms
      </button>
      {open && (
        <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
          <InfoCell label="Deal Structure" value={pretty(offer?.deal_structure)} />
          <InfoCell label="Payment Model" value={pretty(offer?.payment_model)} />
          <InfoCell label="Base Amount" value={offer?.base_amount ? `${offer.currency || 'USD'} ${Number(offer.base_amount).toLocaleString()}` : '—'} />
          <InfoCell label="Recurring" value={offer?.recurring_amount ? `${offer.currency || 'USD'} ${Number(offer.recurring_amount).toLocaleString()}` : '—'} />
          <InfoCell label="Sign-On Bonus" value={offer?.signon_bonus_amount ? `${offer.signon_bonus_currency || 'USD'} ${Number(offer.signon_bonus_amount).toLocaleString()}` : '—'} />
          <InfoCell label="Exclusivity" value={pretty(rights.exclusivity)} />
          <InfoCell label="Territory" value={(rights.territory || []).join(', ')} />
          <InfoCell label="Duration" value={pretty(rights.duration_type)} />
          <InfoCell label="Term" value={`${rights.term_start || ''} → ${rights.term_end || ''}`} />
          <InfoCell label="Expiry Behavior" value={pretty(rights.expiry_behavior)} />
          <InfoCell label="Sublicensing" value={pretty(rights.sublicensing)} />
          <InfoCell label="Usage Scope" value={pretty(rights.usage_scope)} />
          <InfoCell label="Output Reuse" value={pretty(rights.output_reuse)} />
          <InfoCell label="Source Reuse" value={pretty(rights.source_material_reuse)} />
          <InfoCell label="Asset Ownership" value={pretty(rights.asset_ownership)} />
          <InfoCell label="Moral Rights" value={pretty(rights.moral_rights)} />
          <InfoCell label="Endorsement" value={rights.endorsement_requested ? 'Requested' : 'Not requested'} />
          <InfoCell label="Generation Types" value={(rights.generation_types || []).map(t => t.replace(/_/g, ' ')).join(', ')} />
          <InfoCell label="Visual Representation" value={pretty(rights.visual_representation)} />
          <InfoCell label="Permitted Alterations" value={(rights.permitted_alterations || []).map(t => t.replace(/_/g, ' ')).join(', ') || 'None'} />
          <InfoCell label="Voice Synthesis" value={rights.dialogue_permitted ? (rights.dialogue_scripted_only ? 'Scripted only' : 'Permitted') : 'Not permitted'} />
          <InfoCell label="Post-Gen Modifications" value={(rights.post_generation_modifications || []).map(t => t.replace(/_/g, ' ')).join(', ') || '—'} />
          {(offer?.deal_structure === 'subscription' || offer?.deal_structure === 'hybrid') && (
            <>
              <InfoCell label="Billing Frequency" value={pretty(rights.subscription_billing_frequency)} />
              <InfoCell label="Subscription Exclusivity" value={pretty(rights.subscription_exclusivity)} />
              <InfoCell label="Generation Caps" value={rights.generation_caps_enabled ? 'On' : 'Off'} />
              <InfoCell label="Content Reuse" value={pretty(rights.content_reuse_scope)} />
              <InfoCell label="Suspension" value={rights.suspension_permitted ? `Up to ${rights.suspension_max_days || 0}d / ${pretty(rights.suspension_period_basis)}` : 'Not permitted'} />
              <InfoCell label="Price Change" value={pretty(rights.price_change_policy)} />
              <InfoCell label="Agency Cancellation" value={`${rights.agency_cancellation_notice_days || 0}d notice / ${pretty(rights.agency_cancellation_fee)}`} />
              <InfoCell label="Creator Withdrawal" value={`${rights.creator_withdrawal_notice_days || 0}d notice`} />
              <InfoCell label="Post-Sub Assets" value={`${pretty(rights.accumulated_assets_post_subscription)}${rights.accumulated_assets_days ? ` (${rights.accumulated_assets_days}d)` : ''}`} />
            </>
          )}
          <InfoCell label="Termination Triggers" value={(rights.termination_triggers || []).length ? `${rights.termination_triggers.length} selected` : '—'} />
          <InfoCell label="Cure Period" value={pretty(rights.cure_period)} />
          <InfoCell label="Morality Clause" value={rights.morality_clause ? `On (${(rights.morality_conditions || []).length} condition(s))` : 'Off'} />
          <InfoCell label="Early Termination" value={rights.early_termination_permitted ? `${rights.early_termination_notice_days || 0}d notice / ${pretty(rights.early_termination_compensation)}${rights.early_termination_compensation === 'kill_fee' ? ` ${rights.early_termination_kill_fee_percentage || 0}%` : ''}` : 'Not permitted'} />
          <InfoCell label="Takedown Window" value={`${rights.post_termination_takedown_days || 0} days`} />
          <InfoCell label="Deletion Window" value={`${rights.post_termination_deletion_days || 0} days`} />
          <InfoCell label="Residual Use" value={rights.residual_use_permitted ? `${rights.residual_use_days || 0}d, ${(rights.residual_use_channels || []).join(', ')}` : 'Not permitted'} />
          <InfoCell label="Governing Law" value={pretty(rights.governing_law)} />
          <InfoCell label="Dispute Resolution" value={pretty(rights.dispute_resolution)} />
        </div>
      )}
    </div>
  );
}
